import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useBranch } from "../context/BranchContext";
import ApexGraph from "../components/ApexGraph";
import ErrorBoundary from "../components/ErrorBoundary";
import HeatmapChart from "../components/HeatmapChart";
import useHistoricalData, { getRelativeRange } from "../hooks/useHistoricalData";
import useHeatmapData from "../hooks/useHeatmapData";
import SankeyDiagram from "../components/SankeyDiagram";
import DrillDownPieChart from "../components/DrillDownPieChart";
import SolarGraph from "../components/SolarGraph";
import SolarProductionComparison from "../components/SolarProductionComparison";
import { buildSolarRows } from "../utils/solarIdeal";
import ChartBurgerMenu from "../components/ChartBurgerMenu";
import ComparisonGraph from "../components/ComparisonGraph";
import OnOffChart from "../components/OnOffChart";
import AIAnalyzerModal from "../components/AIAnalyzerModal";
import AIAnalyzerButton from "../components/AIAnalyzerButton";
import BoltLoader from "../components/BoltLoader";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import {
  getMachineType,
  getColumnVisibility,
  getMainEnergyLabel,
} from "../utils/machineTypeDetector";
import { getMachineColor } from "../utils/machineColors";
import api from "../utils/api";
import { getConsumerInfo } from "../controllers/userController";
import {
  buildEnergyBreakdownRows,
  fetchDailyEnergySeries,
  fetchEnergyHierarchy,
} from "../utils/energyReportData";
import { fetchHeatmapEnergyData } from "../utils/energyReportHeatmap";
import { generateEnergyBreakdownPdf } from "../utils/energyReportPdf";
import EnergyReportPdfDialog from "../components/EnergyReportPdfDialog";
import FieldHelp from "../help/FieldHelp";
import {
  buildCostRows,
  buildScopeCostSummary,
  fetchReportPricing,
  fetchScopeSummary,
} from "../utils/energyReportCost";
import {
  formatAmount,
  parsePriceNumber,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";
import "./HistoricalTab.css";

// Metrics the PKR toggle can price: power and TAP (kW x rate) and cumulative
// energy (kWh x rate). Voltage, current, power factor, frequency and THD have
// no cost, so they stay in their own units whatever the toggle says.
const PKR_CONVERTIBLE_METRICS = ["power_kw", "tap", "energy"];

function extractStatValue(stat) {
  if (stat == null) return null;
  if (typeof stat === "object") {
    const value = stat.value ?? stat.avg ?? stat.min ?? stat.max;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(stat);
  return Number.isFinite(parsed) ? parsed : null;
}

function extractStatTimestamp(stat) {
  if (!stat || typeof stat !== "object") return null;
  return stat.timestamp || null;
}

function findClosestTimestamp(rawLabels, values, targetValue) {
  const numericTarget = Number(targetValue);
  if (!Number.isFinite(numericTarget)) return null;
  if (!Array.isArray(values) || !values.length) return null;

  let bestIndex = -1;
  let smallestDistance = Number.POSITIVE_INFINITY;

  values.forEach((value, index) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;

    const distance = Math.abs(numericValue - numericTarget);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      bestIndex = index;
    }
  });

  if (bestIndex < 0) return null;
  return rawLabels?.[bestIndex] || null;
}

const quickRanges = [
  { label: "1D", value: 1 },
  { label: "3D", value: 3 },
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
];

const comparisonMetricOptions = [
  {
    label: "Power",
    value: "power_kw",
    unit: "kW",
    matchers: ["[kw]", "power_kw", "power", "_power", "kw"],
  },
  {
    label: "Energy",
    value: "energy",
    unit: "kWh",
    matchers: ["[kwh]", "energy", "kwh", "_energy", "_kwh"],
  },
  {
    label: "Voltage",
    value: "voltage",
    unit: "V",
    matchers: ["[v]", "voltage", "_v"],
  },
  {
    label: "Power Factor",
    value: "power_factor",
    unit: "",
    matchers: ["[pf]", "power factor", "power_factor", "_pf", "pf"],
  },
  {
    label: "Current",
    value: "current",
    unit: "A",
    matchers: ["[a]", "current", "_a", "amps"],
  },
  {
    label: "Frequency",
    value: "frequency",
    unit: "Hz",
    matchers: ["[hz]", "frequency", "_hz", "hz"],
  },
  {
    label: "THD Voltage",
    value: "THD_V",
    unit: "%",
    matchers: ["[thd_v]", "thd_voltage", "thd voltage", "_thd_v", "thd_v", "thdv", "[thdv]", "thd"],
  },
  {
    label: "THD Current",
    value: "THD_I",
    unit: "%",
    matchers: ["[thd_i]", "thd_current", "thd current", "_thd_i", "thd_i", "thdi", "[thdi]"],
  },
  {
    label: "TAP",
    value: "TAP",
    unit: "kW",
    matchers: ["[tap]", "tap", "total active power", "total_active_power", "_tap"],
  },
];

const phaseMetricRegex = /(?:phase\s*-?_?[123]|(?:^|[_[])(?:p|v|i|pf|thdv|thdi)[123]\]?$)/i;

const knownMetricSuffixes = [
  "power_kw",
  "power_factor",
  "frequency",
  "voltage",
  "current",
  "usage",
  "energy",
  "thd_voltage",
  "thd_current",
  "thd_v",
  "thd_i",
  "thdv",
  "thdi",
  "tap",
  // Negative/export counterparts of the energy series. Without these the
  // fallback split turns `Bulb_kWh_neg` into a phantom machine named
  // "Bulb_kWh" that shows up in the machine selector but has no data.
  "kwh_neg",
  "kw_neg",
  "kwh",
  "kw",
  "hz",
  "pf",
  "v",
  "a",
  "[v]",
  "[pf]",
  "[hz]",
  "[a]",
  "[kw]",
  "[kwh]",
  "[thd_v]",
  "[thd_i]",
  "[tap]",
  "p1", "p2", "p3",
  "v1", "v2", "v3",
  "i1", "i2", "i3",
  "pf1", "pf2", "pf3",
  "thdv1", "thdv2", "thdv3",
  "thdi1", "thdi2", "thdi3",
  "rp1", "rp2", "rp3",
  "ap1", "ap2", "ap3",
  "[p1]", "[p2]", "[p3]",
  "[v1]", "[v2]", "[v3]",
  "[i1]", "[i2]", "[i3]",
  "[pf1]", "[pf2]", "[pf3]",
  "[thdv1]", "[thdv2]", "[thdv3]",
  "[thdi1]", "[thdi2]", "[thdi3]",
];

const parseCompositeMetric = (metricKey) => {
  if (!metricKey) return { machineName: "", metricName: "" };
  const lower = String(metricKey).toLowerCase();

  const sortedSuffixes = [...knownMetricSuffixes].sort(
    (a, b) => b.length - a.length,
  );

  for (const suffix of sortedSuffixes) {
    const token = `_${suffix}`;
    if (lower.endsWith(token)) {
      return {
        machineName: metricKey.slice(0, metricKey.length - token.length),
        metricName: metricKey.slice(metricKey.length - suffix.length),
      };
    }
  }

  const idx = metricKey.lastIndexOf("_");
  if (idx === -1) return { machineName: metricKey, metricName: "" };
  return {
    machineName: metricKey.slice(0, idx),
    metricName: metricKey.slice(idx + 1),
  };
};

const formatMetricLabel = (metric) => {
  if (!metric) return "";
  const { machineName, metricName } = parseCompositeMetric(metric);
  if (machineName && metricName) {
    const prettyMetricName = String(metricName).replace(/_/g, " ");
    return `${machineName} (${prettyMetricName})`;
  }
  return metric;
};

const getPhaseMetricKey = (activeMetricValue, phaseNum) => {
  const v = String(activeMetricValue || "").toLowerCase();
  if (v.includes("voltage") || v === "v" || v === "thd_v") {
    if (v.includes("thd")) return `thdv${phaseNum}`;
    return `v${phaseNum}`;
  }
  if (v.includes("current") || v === "i" || v === "a" || v === "thd_i") {
    if (v.includes("thd")) return `thdi${phaseNum}`;
    return `i${phaseNum}`;
  }
  if (v.includes("power_kw") || v === "kw" || v === "p" || v === "tap") {
    return `p${phaseNum}`;
  }
  if (v.includes("power_factor") || v === "pf") {
    return `pf${phaseNum}`;
  }
  if (v.includes("reactive") || v === "rp" || v === "rap") {
    return `rp${phaseNum}`;
  }
  if (v.includes("apparent") || v === "ap" || v === "tp") {
    return `ap${phaseNum}`;
  }
  return `${v}${phaseNum}`;
};

const getSeriesDataForPhase = (machineName, activeMetricValue, phaseNum, seriesByMetric) => {
  if (!seriesByMetric || !machineName) return null;
  const targetSuffix = getPhaseMetricKey(activeMetricValue, phaseNum).toLowerCase();
  const searchPrefix = `${machineName.toLowerCase()}_`;

  const directKey = `${machineName}_${targetSuffix}`;
  if (seriesByMetric[directKey]) {
    return { key: directKey, data: seriesByMetric[directKey] };
  }

  for (const key of Object.keys(seriesByMetric)) {
    const kLower = key.toLowerCase();
    if (kLower.startsWith(searchPrefix)) {
      const suffix = kLower.slice(searchPrefix.length);
      if (
        suffix === targetSuffix ||
        suffix === `[${targetSuffix}]` ||
        suffix === `_${targetSuffix}` ||
        suffix.endsWith(`_${targetSuffix}`)
      ) {
        return { key, data: seriesByMetric[key] };
      }
    }
  }
  return null;
};

const ChartStatus = ({ type = "empty", title, message, actionLabel, onAction }) => {
  const isLoading = type === "loading";
  const isError = type === "error";

  return (
    <div className={`historical-chart-status historical-chart-status-${type}`}>
      {isLoading ? (
        <div className="historical-chart-spinner" aria-hidden="true" />
      ) : (
        <div className="historical-chart-status-icon" aria-hidden="true">
          {isError ? "!" : "i"}
        </div>
      )}
      <div className="historical-chart-status-title">{title}</div>
      {message ? (
        <div className="historical-chart-status-message">{message}</div>
      ) : null}
      {actionLabel && onAction ? (
        <button
          className="historical-chart-status-action"
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};

const HistoricalTab = () => {
  // Historical branch filter: "All" (default) preserves the original single-dropdown
  // behavior; otherwise scopes the node dropdown to machines under the chosen branch.
  const {
    selectedBranch,
    machineToBranchMap,
    meterIdToBranchMap,
    branchList: contextBranchList,
    hierarchyTree: fullHierarchyTree,
  } = useBranch();

  const {
    mode,
    setMode,
    dateRange,
    graphDateRange,
    setDateRangeFromStrings,
    setGraphDateRangeFromStrings,
    selectedMetric,
    setSelectedMetric,
    loading,
    error,
    chartData,
    metrics,
    seriesByMetric,
    refetch,
    fetchSummaryData,
    clearSummaryCache,
    clearFetchCache,
    summaryData,
    setSummaryData,
    summaryLoading,
    avgMonthlyPfData,
    avgMonthlyPfLoading,
    mdiData,
    mdiLoading,
    machinesMdiData,
    selectedMachineNode,
    setSelectedMachineNode,
    hierarchyData,
    machineColors,
    rawLabels,
    stats,
    rawResponse,
  } = useHistoricalData(selectedBranch);

  const [viewMode, setViewMode] = useState("chart"); // "chart" or "heatmap"
  const [comparisonMetric, setComparisonMetric] = useState("power_kw");
  const [draftMetric, setDraftMetric] = useState("power_kw");
  const [draftMachine, setDraftMachine] = useState("Usage");
  const [loadingProgress, setLoadingProgress] = useState(15);
  const [comparisonSelections, setComparisonSelections] = useState({});
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [userSelectionLocked, setUserSelectionLocked] = useState(false);
  const [currencyMode, setCurrencyMode] = useState("Energy"); // "Energy" or "PKR"
  const [graphType, setGraphType] = useState("line"); // Default to line
  const isEnergyMetric = comparisonMetric === "energy";
  // Grid-level Energy is the only metric that carries three series (total /
  // import / export). This picks which one the chart draws; every other
  // category ignores it.
  const [gridEnergyFlow, setGridEnergyFlow] = useState("total");
  const [selectedPhaseMode, setSelectedPhaseMode] = useState("overall"); // "overall", "phase1", "phase2", "phase3", "all"

  useEffect(() => {
    if (
      !selectedMachines ||
      selectedMachines.length !== 1 ||
      (selectedMachineNode && (selectedMachineNode.phase_type === 1 || selectedMachineNode.phase_type === "1"))
    ) {
      setSelectedPhaseMode("overall");
    }
  }, [selectedMachineNode, selectedMachines]);

  // Energy metric is only meaningful as a bar graph — force it when selected.
  // When switching away from Energy, reset to `line` only if the previous
  // metric was Energy (so we don't override an explicit user choice).
  const prevComparisonRef = useRef(comparisonMetric);
  useEffect(() => {
    const prev = prevComparisonRef.current;
    if (comparisonMetric === "energy") {
      setGraphType("bar");
    } else if (prev === "energy" && comparisonMetric !== "energy") {
      // We only auto-reset when coming from energy → other
      setGraphType("line");
    }
    prevComparisonRef.current = comparisonMetric;
  }, [comparisonMetric]);
  const [solarDetected, setSolarDetected] = useState(false);
  const [solarData, setSolarData] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [solarGranularity, setSolarGranularity] = useState("hourly");
  const [solarProductionData, setSolarProductionData] = useState(null);
  const [solarProductionLoading, setSolarProductionLoading] = useState(false);
  // Bumped when the page becomes visible again, so panel settings edited meanwhile
  // (step-2 app) show up in the Ideal series without a manual reload.
  const [solarRefreshKey, setSolarRefreshKey] = useState(0);
  const [selectedSolarMachine, setSelectedSolarMachine] = useState("");
  const [childGraphLoaders, setChildGraphLoaders] = useState({});
  /** Pricing from `client_profiles` via GET /get_customer_info (gen_unitprice, unit_price, …) */
  const [clientProfilePricing, setClientProfilePricing] = useState(null);
  const [pdfReportLoading, setPdfReportLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeMainChart = () => {
    const title = `${activeComparisonOption.label} Analytics`;
    const payload = buildChartPayload({
      chartTitle: title,
      metricName: activeComparisonOption.label,
      unit: effectiveMetricUnit,
      timestamps: comparisonSeries.labels || [],
      datasets: (comparisonSeries.datasets || []).map((d) => ({
        label: d.label,
        data: d.data,
      })),
      dateRange: dateRange,
      aggregation: mode ? `${mode.charAt(0).toUpperCase()}${mode.slice(1)} intervals` : "Hourly intervals",
      tabContext: `Historical ${activeComparisonOption.label} — ${graphType} chart`,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  // --- Branch filtering (Historical) ---
  const branchList = useMemo(
    () => (hierarchyData || []).filter((n) => n && n.is_branch),
    [hierarchyData]
  );

  // Compute solar machines list from hierarchyData, scoped to selectedBranch
  const solarMachines = useMemo(() => {
    const flatten = (nodes) => {
      let flat = [];
      if (!Array.isArray(nodes)) return flat;
      nodes.forEach((node) => {
        if (node.name) flat.push(node);
        if (node.children) flat = flat.concat(flatten(node.children));
      });
      return flat;
    };

    const allNodes = flatten(hierarchyData || []);
    const solarList = [];
    const seen = new Set();

    // Find branch definition if a specific branch is selected
    const currentBranchObj =
      selectedBranch && selectedBranch !== "All"
        ? (branchList || []).find((b) => b.name === selectedBranch) ||
        (contextBranchList || []).find((b) => b.name === selectedBranch)
        : null;

    allNodes.forEach((node) => {
      const category = (node.category || "").toUpperCase();
      if (
        node.name &&
        !seen.has(node.name) &&
        category === "SOLAR"
      ) {
        // If a specific branch is selected, ensure the solar node belongs to that branch
        if (selectedBranch && selectedBranch !== "All") {
          let belongsToBranch = false;
          if (
            currentBranchObj?.hierarchy_path &&
            node.hierarchy_path &&
            node.hierarchy_path.startsWith(currentBranchObj.hierarchy_path) &&
            node.hierarchy_path !== currentBranchObj.hierarchy_path
          ) {
            belongsToBranch = true;
          } else if (
            machineToBranchMap?.[node.name] === selectedBranch ||
            meterIdToBranchMap?.[node.meter_id || node.id] === selectedBranch
          ) {
            belongsToBranch = true;
          }

          if (!belongsToBranch) return;
        }

        solarList.push(node);
        seen.add(node.name);
      }
    });

    return solarList;
  }, [
    hierarchyData,
    selectedBranch,
    branchList,
    contextBranchList,
    machineToBranchMap,
    meterIdToBranchMap,
  ]);

  // Sync selected solar machine when solarMachines or branch changes
  useEffect(() => {
    if (solarMachines.length > 0) {
      if (!selectedSolarMachine || !solarMachines.some((m) => m.name === selectedSolarMachine)) {
        setSelectedSolarMachine(solarMachines[0].name);
      }
    } else {
      setSelectedSolarMachine("");
      setSolarDetected(false);
      setSolarData(null);
      setSolarProductionData(null);
    }
  }, [solarMachines, selectedSolarMachine]);
  const comparisonChartRef = useRef(null);
  const machineListRef = useRef(null);

  const scrollMachineList = (direction) => {
    if (machineListRef.current) {
      const scrollAmount = 300;
      machineListRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const updateChildGraphLoading = useCallback((key, isLoading) => {
    setChildGraphLoaders((prev) => {
      if (prev[key] === isLoading) {
        return prev;
      }
      return {
        ...prev,
        [key]: isLoading,
      };
    });
  }, []);

  // Isolation: Maintain a stable range for other charts that doesn't follow 'second' mode
  const [stableDateRange, setStableDateRange] = useState(dateRange);

  useEffect(() => {
    if (mode !== "second") {
      setStableDateRange(dateRange);
    }
  }, [dateRange, mode]);

  /* The range the top pickers edit. dateRange / graphDateRange are what the fetch
     effects watch, and they are only written on Apply -- otherwise every keystroke in a
     datetime field kicked off a request. */
  const activeRange =
    mode === "second" || mode === "minute" ? graphDateRange : dateRange;
  const [draftRange, setDraftRange] = useState({
    start: activeRange.start,
    end: activeRange.end,
  });

  // Re-sync the draft whenever the committed range moves for a reason other than the
  // pickers: a mode switch, a quick-range chip, or the hook's own resets.
  useEffect(() => {
    setDraftRange({ start: activeRange.start, end: activeRange.end });
  }, [activeRange.start, activeRange.end]);

  useEffect(() => {
    if (selectedMachineNode?.name) {
      setDraftMachine(selectedMachineNode.name);
    }
  }, [selectedMachineNode]);

  useEffect(() => {
    if (comparisonMetric) {
      setDraftMetric(comparisonMetric);
    }
  }, [comparisonMetric]);

  const dateRangeDirty =
    draftRange.start?.getTime() !== activeRange.start?.getTime() ||
    draftRange.end?.getTime() !== activeRange.end?.getTime();

  const currentMachineName = selectAllActive
    ? "__ALL_MACHINES__"
    : selectedMachineNode?.name ||
    (selectedMetric ? parseCompositeMetric(selectedMetric).machineName : "Usage");

  const isFilterDirty =
    dateRangeDirty ||
    draftMachine !== currentMachineName ||
    (draftMachine !== "Usage" && draftMetric !== comparisonMetric) ||
    (draftMachine === "Usage" && comparisonMetric !== "power_kw");

  const canApply = isFilterDirty && isValidRange(draftRange);
  const applyDateRange = useCallback(() => {
    const payload = {
      start: formatDateTimeLocal(draftRange.start),
      end: formatDateTimeLocal(draftRange.end),
    };
    if (mode === "second" || mode === "minute") {
      setGraphDateRangeFromStrings(payload);
    } else {
      setDateRangeFromStrings(payload);
    }
  }, [draftRange, mode, setGraphDateRangeFromStrings, setDateRangeFromStrings]);

  const [solarStartDate, setSolarStartDate] = useState(stableDateRange?.start || new Date());
  const [solarEndDate, setSolarEndDate] = useState(stableDateRange?.end || new Date());

  // Same split for solar: the two solar fetch effects depend on the committed pair only.
  const [solarDraft, setSolarDraft] = useState({
    start: stableDateRange?.start || new Date(),
    end: stableDateRange?.end || new Date(),
  });


  const solarRangeDirty =
    solarDraft.start?.getTime() !== solarStartDate?.getTime() ||
    solarDraft.end?.getTime() !== solarEndDate?.getTime();

  const canApplySolarRange = solarRangeDirty && isValidRange(solarDraft);

  const applySolarRange = useCallback(() => {
    setSolarStartDate(solarDraft.start);
    setSolarEndDate(solarDraft.end);
  }, [solarDraft]);

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) {
      setClientProfilePricing(null);
      return;
    }
    getConsumerInfo(username)
      .then((data) => {
        setClientProfilePricing({
          unit_price: data.unit_price,
          // Peak hours bill at their own rate; the Productivity graph's
          // Peak/Off-Peak split needs it to cost each segment correctly.
          peak_unit_price: data.peak_unit_price ?? data.peak_unitPrice ?? null,
          gen_unitprice:
            data.gen_unitprice ?? data.gen_unitPrice ?? null,
          solar_unitprice:
            data.solar_unitprice ?? data.solar_unitPrice ?? null,
        });
      })
      .catch(() => {
        setClientProfilePricing(null);
      });
  }, []);

  // Determine machine type from selected node
  const selectedMachineType = useMemo(() => {
    if (!selectedMachineNode) return "building";
    return getMachineType(selectedMachineNode);
  }, [selectedMachineNode]);

  // Get main energy card label
  const mainEnergyLabel = useMemo(() => {
    return getMainEnergyLabel(selectedMachineType);
  }, [selectedMachineType]);

  const unitPrice = useMemo(() => {
    const category = (selectedMachineNode?.category || "").toUpperCase();
    const fromDb =
      category === "GENERATOR"
        ? parsePriceNumber(
          clientProfilePricing?.gen_unitprice ??
          clientProfilePricing?.gen_unitPrice,
        )
        : parsePriceNumber(clientProfilePricing?.unit_price);
    if (fromDb != null) return fromDb;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      let priceStr = user?.unit_price;
      if (category === "GENERATOR") {
        priceStr =
          user?.gen_unitPrice ?? user?.gen_unitprice ?? priceStr;
      }
      const price = parseFloat(priceStr);
      return Number.isFinite(price) ? price : 0;
    } catch {
      return 0;
    }
  }, [selectedMachineNode, clientProfilePricing]);

  const isPkrMode = currencyMode === "PKR";

  /**
   * Every per-kWh rate the page needs (grid / peak / genset / solar / export).
   * `unitPrice` above stays the single rate for the selected node's own cards;
   * the breakdown charts need the whole set because one chart can mix sources.
   */
  const tariffs = useMemo(
    () => resolveTariffs(clientProfilePricing),
    [clientProfilePricing],
  );



  // Solar Detection API Call
  useEffect(() => {
    const checkSolarDetection = async () => {
      const username = localStorage.getItem("username");

      if (!username || !selectedSolarMachine || solarMachines.length === 0) {
        setSolarLoading(false);
        setSolarDetected(false);
        setSolarData(null);
        return;
      }

      console.log("Checking Solar Detection for machine:", selectedSolarMachine);

      const formatDateTime = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        const seconds = `${date.getSeconds()}`.padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      try {
        setSolarLoading(true);
        const apiMode = solarGranularity === "daily" ? "hourly" : solarGranularity;

        const response = await api.get("/solar-data", {
          params: {
            username,
            machine_name: selectedSolarMachine,
            start_date: formatDateTime(solarStartDate),
            end_date: formatDateTime(solarEndDate),
            mode: apiMode,
          },
        });

        // If API says detected, keep it true and set data
        const apiDetected = response.data?.solar_detected || false;

        if (apiDetected) {
          setSolarDetected(true);
          setSolarData(response.data);
        } else {
          setSolarDetected(false);
          setSolarData(null);
        }
      } catch (error) {
        console.error("Error checking solar detection:", error);
        setSolarDetected(false);
        setSolarData(null);
      } finally {
        setSolarLoading(false);
      }
    };

    checkSolarDetection();
  }, [
    selectedSolarMachine,
    solarStartDate,
    solarEndDate,
    solarGranularity,
  ]);



  // Solar Production Data Fetching (Expected vs Actual)
  useEffect(() => {
    const fetchSolarProductionData = async () => {
      const username = localStorage.getItem("username");

      console.log("[Solar Production Fetch] Triggered with:", {
        username,
        selectedSolarMachine,
        solarDetected,
      });

      if (!username || !selectedSolarMachine) {
        setSolarProductionLoading(false);
        setSolarProductionData(null);
        return;
      }

      const formatDateTime = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        const hours = `${date.getHours()}`.padStart(2, "0");
        const minutes = `${date.getMinutes()}`.padStart(2, "0");
        const seconds = `${date.getSeconds()}`.padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      try {
        setSolarProductionLoading(true);
        const startFormatted = formatDateTime(solarStartDate);
        const endFormatted = formatDateTime(solarEndDate);

        console.log("[Solar Production API] Calling with params:", {
          username,
          machine_name: selectedSolarMachine,
          start_datetime: startFormatted,
          end_datetime: endFormatted,
        });

        const response = await api.get("/solar-production", {
          params: {
            username,
            machine_name: selectedSolarMachine,
            start_datetime: startFormatted,
            end_datetime: endFormatted,
          },
        });

        console.log("[Solar Production API] Response:", response.data);
        console.log("[Solar Production API] Array lengths:", {
          timestamps: response.data?.timestamps?.length,
          solar_energy_production: response.data?.solar_energy_production?.length,
          energy_negative_kwh: response.data?.energy_negative_kwh?.length,
        });
        console.log("[Solar Production API] First 3 items:", {
          timestamps: response.data?.timestamps?.slice(0, 3),
          solar_energy_production: response.data?.solar_energy_production?.slice(0, 3),
          energy_negative_kwh: response.data?.energy_negative_kwh?.slice(0, 3),
        });

        // The ideal is calculated here from the hour's sunlight and the machine's
        // current panel settings (utils/solarIdeal.js), so editing efficiency or area
        // changes it immediately. null = no reading: the chart draws a gap, not a zero.
        const transformedData = buildSolarRows(response.data);

        console.log("[Solar Production] Transformed data (first 5):", transformedData.slice(0, 5));
        setSolarProductionData({ data: transformedData });
      } catch (error) {
        console.error("Error fetching solar production data:", error);
        setSolarProductionData(null);
      } finally {
        setSolarProductionLoading(false);
      }
    };

    // Only fetch if solar is detected
    if (solarDetected) {
      fetchSolarProductionData();
    }
  }, [
    selectedSolarMachine,
    solarStartDate,
    solarEndDate,
    solarDetected,
    solarRefreshKey,
  ]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setSolarRefreshKey((k) => k + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);


  const activeComparisonOption = useMemo(
    () =>
      comparisonMetricOptions.find((opt) => opt.value === comparisonMetric) ||
      comparisonMetricOptions[0],
    [comparisonMetric],
  );

  // 1 when the toggle is off or the metric has no cost, so call sites can
  // multiply unconditionally.
  const pkrMultiplier =
    isPkrMode && PKR_CONVERTIBLE_METRICS.includes(activeComparisonOption.value)
      ? unitPrice
      : 1;

  // Unit the main chart is actually showing — used for its axis, exports and
  // the AI payload so none of them claim kWh over costed values.
  const effectiveMetricUnit =
    pkrMultiplier !== 1 ? "PKR" : activeComparisonOption.unit;

  // No-op for summaryCards, now using direct rendering for tables

  const {
    rows: rawHeatmapRows,
    timeLabels: heatmapTimeLabels,
    minValue: rawHeatmapMin,
    maxValue: rawHeatmapMax,
    loading: heatmapLoading,
    hasData: hasHeatmapData,
  } = useHeatmapData(seriesByMetric[selectedMetric] || [], rawLabels || []);

  /* Deliberately excludes the solar loaders: they cover one section, and routing them
     into a fixed full-screen overlay meant changing the solar range greyed out the whole
     tab. Solar renders `isSolarLoading` inside its own section instead. */
  const isAnyGraphLoading = useMemo(() => {
    const childLoading = Object.values(childGraphLoaders).some(Boolean);
    return (
      loading ||
      childLoading
    );
  }, [
    childGraphLoaders,
    loading,
  ]);

  // Loading progress ticker (smoothly increments from 15% to 100% based on active loaders)
  useEffect(() => {
    let progressInterval = null;

    if (isAnyGraphLoading) {
      setLoadingProgress((prev) => (prev > 0 && prev < 90 ? prev : 15));

      const totalLoaders = 5 + Object.keys(childGraphLoaders).length;
      let activeCount = 0;
      if (loading) activeCount++;
      if (summaryLoading) activeCount++;
      if (heatmapLoading) activeCount++;
      if (avgMonthlyPfLoading) activeCount++;
      if (mdiLoading) activeCount++;
      Object.values(childGraphLoaders).forEach((v) => {
        if (v) activeCount++;
      });

      const completedRatio = Math.max(
        0,
        (totalLoaders - activeCount) / totalLoaders
      );
      const targetProgress = Math.min(
        92,
        Math.max(25, Math.round(completedRatio * 80) + 15)
      );

      progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev < targetProgress) {
            return Math.min(
              targetProgress,
              prev + Math.max(1, Math.ceil((targetProgress - prev) * 0.3))
            );
          }
          if (prev < 92) {
            return prev + 0.4;
          }
          return prev;
        });
      }, 100);
    } else {
      setLoadingProgress(100);
      const timeout = setTimeout(() => {
        setLoadingProgress(0);
      }, 350);
      return () => clearTimeout(timeout);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [
    isAnyGraphLoading,
    loading,
    summaryLoading,
    heatmapLoading,
    avgMonthlyPfLoading,
    mdiLoading,
    childGraphLoaders,
  ]);

  const isSolarLoading = solarLoading || solarProductionLoading;

  const { heatmapRows, heatmapMin, heatmapMax } = useMemo(() => {
    if (!rawHeatmapRows || !hasHeatmapData)
      return { heatmapRows: [], heatmapMin: 0, heatmapMax: 0 };

    if (pkrMultiplier !== 1) {
      const multiplier = pkrMultiplier;
      const convertedRows = rawHeatmapRows.map((row) => ({
        ...row,
        values: row.values.map((v) =>
          typeof v === "number" ? v * multiplier : v,
        ),
      }));
      return {
        heatmapRows: convertedRows,
        heatmapMin:
          typeof rawHeatmapMin === "number"
            ? rawHeatmapMin * multiplier
            : rawHeatmapMin,
        heatmapMax:
          typeof rawHeatmapMax === "number"
            ? rawHeatmapMax * multiplier
            : rawHeatmapMax,
      };
    }

    return {
      heatmapRows: rawHeatmapRows,
      heatmapMin: rawHeatmapMin,
      heatmapMax: rawHeatmapMax,
    };
  }, [
    rawHeatmapRows,
    hasHeatmapData,
    pkrMultiplier,
    rawHeatmapMin,
    rawHeatmapMax,
  ]);

  // Consolidate machine list from hierarchy and metrics, add 'Usage' as special option
  const machinesList = useMemo(() => {
    const flatten = (nodes) => {
      let flat = [];
      if (!Array.isArray(nodes)) return flat;
      nodes.forEach((node) => {
        if (node.name) flat.push(node);
        if (node.children) flat = flat.concat(flatten(node.children));
      });
      return flat;
    };

    const allHierarchyNodes = flatten(hierarchyData || []);
    const seenNames = new Set();
    const result = [];

    // machines-data synthesizes a "<Branch> Usage" series per branch (the sum of
    // that branch's GRID meters) and returns it alongside the real meters, so it
    // leaks into `metrics` below. Those names exist only as response columns —
    // there is no client_machines row — so every machine-scoped endpoint returns
    // zeros for them (/summary reports 0 kWh/kW while the chart still draws the
    // series). The Branch dropdown is the supported way to scope to a branch, so
    // keep the aggregates out of the Node dropdown entirely.
    const branchAggregateNames = new Set(
      allHierarchyNodes
        .filter((node) => node?.is_branch && node.name)
        .map((node) => `${node.name} Usage`.toLowerCase())
    );

    // 1. Add nodes from hierarchy
    allHierarchyNodes.forEach((node) => {
      const category = (node.category || "").toUpperCase();
      // Exclude ENVIRONMENT category nodes from the machine selector
      if (
        node.name &&
        !seenNames.has(node.name) &&
        category !== "ENVIRONMENT"
      ) {
        result.push(node);
        seenNames.add(node.name);
      }
    });

    // 2. Add machines from metrics as fallback
    metrics.forEach((m) => {
      const { machineName: name } = parseCompositeMetric(m);
      const upperName = name.toUpperCase();
      if (
        name &&
        !seenNames.has(name) &&
        !branchAggregateNames.has(name.toLowerCase()) &&
        name !== "Date & Time" &&
        !upperName.includes("ENVIRONMENT")
      ) {
        result.push({ name, category: "Unknown", children: [] });
        seenNames.add(name);
      }
    });

    // Add 'Usage' as a special option if not already present AND a GRID machine exists
    const hasGrid = allHierarchyNodes.some(
      (node) => (node.category || "").toUpperCase() === "GRID"
    );
    if (hasGrid && !seenNames.has("Usage")) {
      result.unshift({ name: "Usage", category: "Special", children: [] });
      seenNames.add("Usage");
    }

    return result.sort((a, b) => {
      // Keep 'Usage' always at the top
      if (a.name === "Usage") return -1;
      if (b.name === "Usage") return 1;
      const getPriority = (node) => {
        const cat = (node.category || "").toUpperCase();
        if (cat === "GRID") return 0;
        if (cat === "SOLAR") return 1;
        if (cat === "GENERATOR") return 2;
        if (cat === "MACHINE") return 3;
        return 4;
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [hierarchyData, metrics]);

  // --- Branch filtering (Historical) ---
  // Branches are VIRTUAL nodes flagged is_branch in Step 2. The Branch dropdown
  // picks one; the Node dropdown then lists the machines under it (hierarchy_path
  // descendants). "All" keeps the original single-dropdown behavior and is the
  // only option when the client has no branches.

  const nodesUnderBranch = useCallback(
    (branchName) => {
      if (!branchName || branchName === "All") return machinesList;
      const b = branchList.find((x) => x.name === branchName);
      if (!b || !b.hierarchy_path) return [];
      // Machines under this branch (its hierarchy_path descendants).
      const branchDescendants = machinesList.filter(
        (m) =>
          m.hierarchy_path &&
          m.hierarchy_path.startsWith(b.hierarchy_path) &&
          m.hierarchy_path !== b.hierarchy_path
      );
      const branchUsageNode = {
        name: "Usage",
        displayName: `${branchName} (Usage)`,
        category: "USAGE",
        is_branch: false,
        isBranchUsage: true,
        branch: branchName,
      };
      return [branchUsageNode, ...branchDescendants];
    },
    [machinesList, branchList]
  );

  const nodeOptions = useMemo(
    () =>
      nodesUnderBranch(selectedBranch).filter(
        (m) => (m.category || "").toUpperCase() !== "ENVIRONMENT"
      ),
    [nodesUnderBranch, selectedBranch]
  );

  // Filtered options specifically for the top line graph: exclude branch, solar, and virtual machines
  const topLineGraphNodeOptions = useMemo(
    () =>
      nodeOptions.filter((m) => {
        if (!m) return false;
        if (m.is_branch) return false;
        const cat = (m.category || "").trim().toUpperCase();
        return (
          cat !== "BRANCH" &&
          cat !== "SOLAR" &&
          cat !== "VIRTUAL"
        );
      }),
    [nodeOptions]
  );

  // Machine names under the selected branch, for scoping the below charts
  // (Peak/Off-Peak, ON/OFF, By Source, Energy Breakdown, Energy Flow). null when
  // "All Branches" so those charts stay facility-wide (unchanged behavior).
  const scopeMachineNames = useMemo(
    () =>
      selectedBranch === "All"
        ? null
        : nodeOptions.map((n) => n.name).filter((n) => n && n !== "Usage"),
    [selectedBranch, nodeOptions]
  );

  // The selected branch's hierarchy_path, for scoping the hierarchical charts
  // (Energy Breakdown pie, Energy Flow sankey) to the branch's subtree.
  const scopeHierarchyPath = useMemo(
    () =>
      selectedBranch === "All"
        ? null
        : branchList.find((b) => b.name === selectedBranch)?.hierarchy_path || null,
    [selectedBranch, branchList]
  );

  // Initialize selectedMachines when machinesList loads
  useEffect(() => {
    if (!machinesList || machinesList.length === 0) return;
    // If already set, do not override user's selection
    if (selectedMachines && selectedMachines.length) return;

    if (selectedBranch && selectedBranch !== "All") {
      setSelectAllActive(false);
      const branchUsageNode = nodeOptions.find((n) => n.name === "Usage");
      if (branchUsageNode) {
        setSelectedMachines(["Usage"]);
        handleMachineSelect(branchUsageNode);
        setDraftMachine("Usage");
        return;
      }
      const firstValidNode = nodeOptions.find((n) => n.name !== "Usage");
      if (firstValidNode) {
        setSelectedMachines([firstValidNode.name]);
      }
      return;
    }

    // Default: only "Usage" (matches the metric dropdown). Fall back to a single
    // GRID/MACHINE entry only when there is no Usage option.
    const defaults = [];
    let defaultNode = null;
    const usageNode = machinesList.find((m) => m.name === "Usage");

    if (usageNode) {
      defaults.push("Usage");
      defaultNode = usageNode;
    } else {
      const firstGrid = machinesList.find((m) => (m.category || "").toUpperCase() === "GRID");
      const firstMachine = machinesList.find((m) => (m.category || "").toUpperCase() === "MACHINE");
      if (firstGrid) {
        defaults.push(firstGrid.name);
        defaultNode = firstGrid;
      } else if (firstMachine) {
        defaults.push(firstMachine.name);
        defaultNode = firstMachine;
      }
    }

    if (defaults.length) {
      setSelectedMachines(defaults);
      if (defaultNode) {
        handleMachineSelect(defaultNode);
        setDraftMachine(defaultNode.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machinesList, selectedBranch, nodeOptions]);

  useEffect(() => {
    if (!selectAllActive || !topLineGraphNodeOptions.length) return;

    const allNames = topLineGraphNodeOptions.map((machine) => machine.name);
    const currentKey = selectedMachines.join("|");
    const nextKey = allNames.join("|");

    if (currentKey !== nextKey) {
      setSelectedMachines(allNames);
    }
  }, [selectAllActive, topLineGraphNodeOptions, selectedMachines]);

  const availableComparisonMetrics = useMemo(() => {
    if (!metrics.length) return [];
    const isMatch = (metricName) =>
      metricName &&
      activeComparisonOption.matchers.some((matcher) =>
        metricName.toLowerCase().includes(matcher.toLowerCase().trim()),
      );
    // Initial filter based on matchers and exclude phase metrics
    const matched = metrics.filter(
      (metric) => isMatch(metric) && !phaseMetricRegex.test(metric),
    );
    // Deduplicate by machine name (prefix before first underscore)
    const seen = new Set();
    const deduped = [];
    matched.forEach((m) => {
      const { machineName } = parseCompositeMetric(m);
      const upperMachineName = machineName.toUpperCase();
      // Skip if machine is categorized as ENVIRONMENT or name contains ENVIRONMENT
      const machineNode = machinesList.find(
        (node) => node.name === machineName,
      );
      const isEnvironment =
        (machineNode &&
          (machineNode.category || "").toUpperCase() === "ENVIRONMENT") ||
        upperMachineName.includes("ENVIRONMENT");

      if (!seen.has(machineName) && !isEnvironment) {
        seen.add(machineName);
        deduped.push(m);
      }
    });
    return deduped;
  }, [metrics, activeComparisonOption, machinesList]);

  // Sync selectedMetric with available options for the category
  useEffect(() => {
    if (availableComparisonMetrics.length > 0) {
      if (!availableComparisonMetrics.includes(selectedMetric)) {
        // Only override if the currently selected one is NOT in the new list
        const currentMachine = selectedMetric
          ? parseCompositeMetric(selectedMetric).machineName
          : selectedMachineNode?.name || "Usage";

        const sameMachineMetric = availableComparisonMetrics.find(
          (m) =>
            m.startsWith(`${currentMachine}_`) || m.startsWith(currentMachine),
        );
        if (sameMachineMetric) {
          setSelectedMetric(sameMachineMetric);
        } else {
          setSelectedMetric(availableComparisonMetrics[0]);
        }
      }
    }
  }, [
    availableComparisonMetrics,
    selectedMetric,
    setSelectedMetric,
    selectedMachineNode,
  ]);

  // Specific sync for Heatmap when selectedMachineNode changes
  useEffect(() => {
    if (selectedMachineNode?.name === "Usage") return; // Skip sync for Usage
    if (selectedMachineNode && metrics.length > 0) {
      const machineName = selectedMachineNode.name;
      const machineMetrics = metrics.filter((m) =>
        m.startsWith(`${machineName}_`),
      );
      if (machineMetrics.length > 0) {
        // Try to stay on the same metric type if possible, otherwise default to first machine metric
        const currentMetricPart = selectedMetric?.split("_")?.[1] || "";
        const match = machineMetrics.find((m) =>
          m.endsWith(`_${currentMetricPart}`),
        );
        if (match) {
          setSelectedMetric(match);
        } else {
          const preferred = machineMetrics.find(
            (m) =>
              m.toLowerCase().includes("usage") ||
              m.toLowerCase().includes("kw") ||
              m.toLowerCase().includes("power"),
          );
          setSelectedMetric(preferred || machineMetrics[0]);
        }
      }
    }
  }, [selectedMachineNode, metrics, selectedMetric, setSelectedMetric]);

  useEffect(() => {
    // Use functional updater so we don't need comparisonSelections in deps
    // (including it caused an infinite re-render loop).
    if (!availableComparisonMetrics.length) {
      setComparisonSelections((prev) =>
        Object.keys(prev || {}).length === 0 ? prev : {},
      );
      return;
    }
    if (!selectedMachines || selectedMachines.length === 0) {
      setComparisonSelections((prev) =>
        Object.keys(prev || {}).length === 0 ? prev : {},
      );
      return;
    }

    setComparisonSelections((prev) => {
      // Do not overwrite an active multi-selection; only initialize when empty.
      if (Object.keys(prev || {}).length > 0) {
        return prev;
      }
      const next = {};
      availableComparisonMetrics.forEach((metric) => {
        next[metric] = metric === selectedMetric;
      });
      const anyTrue = Object.values(next).some(Boolean);
      if (!anyTrue && availableComparisonMetrics.length) {
        next[availableComparisonMetrics[0]] = true;
      }
      return next;
    });
  }, [availableComparisonMetrics, selectedMetric, selectedMachines]);

  const gridMachineNames = useMemo(
    () =>
      new Set(
        (hierarchyData || [])
          .filter((node) => (node.category || "").toUpperCase() === "GRID")
          .map((node) => node.name),
      ),
    [hierarchyData],
  );

  // Only surface the flow selector when the chart is scoped to GRID machines
  // alone under the Energy metric. A mixed selection ("All Machines") is
  // excluded: the flow only exists for grid nodes, so switching it there would
  // silently drop the grid series while every other machine kept its total.
  const showGridEnergyFlow = useMemo(() => {
    if (activeComparisonOption.value !== "energy") return false;
    const charted = Object.keys(comparisonSelections || {}).filter(
      (metric) => comparisonSelections[metric],
    );
    if (!charted.length) return false;
    return charted.every((metric) =>
      gridMachineNames.has(parseCompositeMetric(metric).machineName),
    );
  }, [activeComparisonOption, comparisonSelections, gridMachineNames]);

  const comparisonSeries = useMemo(() => {
    const labels = chartData?.labels?.length ? chartData.labels : (rawLabels || []);
    if (!labels.length || !availableComparisonMetrics.length) {
      return {
        labels: labels || [],
        datasets: [],
        option: activeComparisonOption,
      };
    }
    const primaryMachineName = selectedMachineNode?.name || "Usage";
    const effectiveMachines = selectedMachines && selectedMachines.length > 0
      ? (selectedMachines.includes(primaryMachineName) ? selectedMachines : [primaryMachineName, ...selectedMachines])
      : [primaryMachineName];

    // Use the explicit keys in comparisonSelections, filtered strictly to currently active machines,
    // so unselecting an additional machine immediately restores single-series min/max limits.
    let selectedMetrics = Object.keys(comparisonSelections || {}).filter(
      (metric) => {
        if (!comparisonSelections[metric]) return false;
        const { machineName } = parseCompositeMetric(metric);
        return effectiveMachines.includes(machineName);
      },
    );

    // If comparisonSelections does not contain any valid metric matching the active option yet
    // (e.g. metric just changed and effect hasn't committed next selections), derive them immediately
    // so datasets populate on this render without flashing an empty state.
    const hasActiveMetricInSelections = selectedMetrics.some((m) =>
      availableComparisonMetrics.includes(m) ||
      activeComparisonOption.matchers.some((matcher) =>
        m.toLowerCase().includes(matcher.toLowerCase().trim())
      )
    );

    if (!hasActiveMetricInSelections && availableComparisonMetrics.length > 0) {
      const effectiveMachines = selectedMachines.length
        ? selectedMachines
        : [selectedMachineNode?.name || "Usage"];
      const immediateMetrics = [];
      effectiveMachines.forEach((machineName) => {
        const candidate = availableComparisonMetrics.find(
          (m) => m.startsWith(`${machineName}_`) || m.startsWith(machineName)
        );
        if (candidate) immediateMetrics.push(candidate);
      });
      if (immediateMetrics.length > 0) {
        selectedMetrics = immediateMetrics;
      } else if (availableComparisonMetrics[0]) {
        selectedMetrics = [availableComparisonMetrics[0]];
      }
    }

    const datasets = [];
    selectedMetrics.forEach((metric) => {
      const { machineName } = parseCompositeMetric(metric);

      // Use stable name-hash color so toggling machine badges never
      // re-orders other machines' colors (getMachineColor checks
      // machineColors first, then falls back to a deterministic hue
      // derived from the machine name — same logic used by PieChart/Sankey).
      const borderColor = getMachineColor(machineName, machineColors);

      let backgroundColor;
      if (borderColor.startsWith("#")) {
        backgroundColor = borderColor.length === 7 ? `${borderColor}2E` : borderColor;
      } else {
        // hsl(…) → hsla(…, 0.18)
        backgroundColor = borderColor.replace(/^hsl\(/, "hsla(").replace(/\)$/, ", 0.18)");
      }

      let data = seriesByMetric[metric] || [];

      // Cost the series when the toggle is on and the metric has a price
      if (pkrMultiplier !== 1) {
        data = data.map((v) => (typeof v === "number" ? v * pkrMultiplier : v));
      }

      // GRID + Energy is the only combination that splits into total / import /
      // export; anything else keeps drawing its single series untouched.
      const isGridEnergy =
        activeComparisonOption.value === "energy" &&
        gridMachineNames.has(machineName);

      if (!isGridEnergy || gridEnergyFlow === "total") {
        if (selectedPhaseMode !== "overall" && activeComparisonOption.value !== "energy") {
          if (selectedPhaseMode === "all") {
            const phaseColors = [
              { border: "#033f63", bg: "#033f632E", label: "Phase 1" },
              { border: "#59AB75", bg: "#59AB752E", label: "Phase 2" },
              { border: "#686B4A", bg: "#686B4A2E", label: "Phase 3" },
            ];
            [1, 2, 3].forEach((pNum, pIdx) => {
              const pSeries = getSeriesDataForPhase(
                machineName,
                activeComparisonOption.value,
                pNum,
                seriesByMetric
              );
              let pData = pSeries ? pSeries.data : [];
              if (pkrMultiplier !== 1) {
                pData = pData.map((v) => (typeof v === "number" ? v * pkrMultiplier : v));
              }
              datasets.push({
                label: `${machineName} (${phaseColors[pIdx].label})`,
                data: pData,
                borderColor: phaseColors[pIdx].border,
                backgroundColor: phaseColors[pIdx].bg,
                tension: 0.35,
                fill: true,
                pointRadius: 4,
              });
            });
          } else {
            const pNum =
              selectedPhaseMode === "phase1"
                ? 1
                : selectedPhaseMode === "phase2"
                  ? 2
                  : 3;
            const pLabel =
              pNum === 1 ? "1st Phase" : pNum === 2 ? "2nd Phase" : "3rd Phase";
            const pSeries = getSeriesDataForPhase(
              machineName,
              activeComparisonOption.value,
              pNum,
              seriesByMetric
            );
            let pData = pSeries ? pSeries.data : [];
            if (pkrMultiplier !== 1) {
              pData = pData.map((v) => (typeof v === "number" ? v * pkrMultiplier : v));
            }
            datasets.push({
              label: `${machineName} (${pLabel})`,
              data: pData,
              borderColor,
              backgroundColor,
              tension: 0.35,
              fill: true,
              pointRadius: 4,
            });
          }
        } else {
          datasets.push({
            label: formatMetricLabel(metric),
            data,
            borderColor,
            backgroundColor,
            tension: 0.35,
            fill: true,
            pointRadius: 4,
          });
        }
      }

      if (isGridEnergy && gridEnergyFlow === "import") {
        // Import mirrors the machine's own energy series.
        datasets.push({
          label: `${machineName} (Import)`,
          data: data,
          borderColor: "#ef4444", // Red for import
          backgroundColor: "#ef44442E",
          tension: 0.35,
          fill: true,
          pointRadius: 4,
        });
      }

      if (isGridEnergy && gridEnergyFlow === "export") {
        // Search for negative metric (Export) in all series keys
        const negMetricKey = Object.keys(seriesByMetric).find(
          (k) =>
            k.startsWith(machineName + "_") &&
            k.toLowerCase().includes("neg") &&
            k.toLowerCase().includes("kwh"),
        );

        if (negMetricKey && seriesByMetric[negMetricKey]) {
          const exportData = seriesByMetric[negMetricKey].map((v) =>
            v != null ? Math.abs(v) : null,
          );
          datasets.push({
            label: `${machineName} (Export)`,
            data: exportData,
            borderColor: "#22c55e", // Green for export
            backgroundColor: "#22c55e2E",
            tension: 0.35,
            fill: true,
            pointRadius: 4,
          });
        }
      }
    });

    // For Energy metric, sort machines by total consumption (descending)
    const finalDatasets =
      activeComparisonOption.value === "energy"
        ? [...datasets].sort((a, b) => {
          const sumA = (a.data || []).reduce(
            (acc, v) => acc + (typeof v === "number" ? v : 0),
            0,
          );
          const sumB = (b.data || []).reduce(
            (acc, v) => acc + (typeof v === "number" ? v : 0),
            0,
          );
          return sumB - sumA;
        })
        : datasets;

    return {
      labels: chartData.labels,
      rawLabels: rawLabels,
      datasets: finalDatasets,
      option: activeComparisonOption,
    };
  }, [
    chartData.labels,
    rawLabels,
    availableComparisonMetrics,
    comparisonSelections,
    seriesByMetric,
    activeComparisonOption,
    pkrMultiplier,
    machineColors,
    gridMachineNames,
    gridEnergyFlow,
    selectedPhaseMode,
  ]);

  const hasComparisonChartData = useMemo(
    () =>
      comparisonSeries.datasets.length > 0 &&
      comparisonSeries.datasets.some((ds) =>
        (ds.data || []).some((value) => Number.isFinite(Number(value))),
      ),
    [comparisonSeries.datasets],
  );

  // Check whether current metrics in memory actually match the active comparison metric
  const isTransitioningMetric = useMemo(() => {
    if (!comparisonMetric || !metrics.length) return false;
    const hasMatchingMetric = metrics.some((m) =>
      activeComparisonOption?.matchers?.some((matcher) =>
        m.toLowerCase().includes(matcher.toLowerCase().trim())
      )
    );
    return !hasMatchingMetric;
  }, [comparisonMetric, activeComparisonOption, metrics]);

  // Initial synchronization check: keep showing loading while initial machines/selections/summary are settling
  // or while metric is transitioning so the user never briefly sees "No data available in this range".
  const isInitialSync =
    !hasComparisonChartData &&
    !error &&
    (isTransitioningMetric || !summaryData || !selectedMachines.length || Object.keys(comparisonSelections).length === 0);

  const comparisonGraphLoading = loading || summaryLoading || isInitialSync;
  const comparisonGraphError = error
    ? "Unable to load graph data. Please check the selected range and try again."
    : "";

  const comparisonStatLines = useMemo(() => {
    const singleSeries = comparisonSeries.datasets.length === 1;
    if (!singleSeries || graphType !== "line" || selectedPhaseMode !== "overall") return [];

    const primaryDataset = comparisonSeries.datasets[0];
    const primaryValues = primaryDataset?.data || [];
    if (!primaryValues.length) return [];

    const validValues = primaryValues
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    if (!validValues.length) return [];

    const isUsage = !selectedMachineNode || selectedMachineNode?.name === "Usage";
    const summaryBucket = (!isUsage && (summaryData?.Consumed || summaryData?.Generated)) || null;

    const minValue = summaryBucket
      ? extractStatValue(summaryBucket.min)
      : Math.min(...validValues);
    const maxValue = summaryBucket
      ? extractStatValue(summaryBucket.max)
      : Math.max(...validValues);
    const averageValue = summaryBucket
      ? extractStatValue(summaryBucket.avg)
      : validValues.reduce((a, b) => a + b, 0) / validValues.length;

    return [
      {
        label: "Max",
        value: maxValue,
        timestamp: summaryBucket
          ? extractStatTimestamp(summaryBucket.max)
          : findClosestTimestamp(comparisonSeries.rawLabels, primaryValues, maxValue),
        color: "#ef4444",
        dashArray: 8,
      },
      {
        label: "Average",
        value: averageValue,
        timestamp: findClosestTimestamp(
          comparisonSeries.rawLabels,
          primaryValues,
          averageValue,
        ),
        color: "#f59e0b",
        dashArray: 8,
      },
      {
        label: "Min",
        value: minValue,
        timestamp: summaryBucket
          ? extractStatTimestamp(summaryBucket.min)
          : findClosestTimestamp(comparisonSeries.rawLabels, primaryValues, minValue),
        color: "#3b82f6",
        dashArray: 8,
      },
    ].filter((line) => Number.isFinite(Number(line.value)));
  }, [comparisonSeries, graphType, summaryData, selectedPhaseMode, selectedMachineNode]);

  // Effect 1: Sync comparisonSelections when machines/metrics/options change.
  // NOTE: Does NOT call refetch() — that is handled by a separate effect to prevent
  // availableComparisonMetrics (derived from API response) from triggering infinite re-fetches.
  useEffect(() => {
    const primaryMachineName = selectedMachineNode?.name || "Usage";
    const baseSelected =
      selectedMachines && selectedMachines.length > 0
        ? selectedMachines
        : [primaryMachineName];
    const effectiveSelectedMachines = baseSelected.includes(primaryMachineName)
      ? baseSelected
      : [primaryMachineName, ...baseSelected];

    const next = {};
    const preserveUsage =
      !userSelectionLocked &&
      (primaryMachineName === "Usage" ||
        (selectedMetric || "").toLowerCase().startsWith("usage_"));
    const normalizedSelectedMachines = preserveUsage
      ? Array.from(new Set(["Usage", ...effectiveSelectedMachines]))
      : effectiveSelectedMachines;

    // For each selected machine, try to pick the best matching metric for the current comparisonMetric.
    normalizedSelectedMachines.forEach((machineName) => {
      if (!machineName) return;

      // 1) Prefer a metric from availableComparisonMetrics that starts with machineName
      let candidate = availableComparisonMetrics.find(
        (m) => m.startsWith(`${machineName}_`) || m.startsWith(machineName),
      );
      // 2) Otherwise, prefer any metric that starts with machineName and matches matchers
      if (!candidate) {
        candidate = metrics.find(
          (m) =>
            (m.startsWith(`${machineName}_`) || m.startsWith(machineName)) &&
            activeComparisonOption.matchers.some((matcher) =>
              m.toLowerCase().includes(matcher.toLowerCase().trim()),
            ),
        );
      }
      // 3) Otherwise, take any metric for the machine
      if (!candidate) {
        candidate = metrics.find(
          (m) => m.startsWith(`${machineName}_`) || m.startsWith(machineName),
        );
      }
      if (candidate) next[candidate] = true;
    });

    // If no matches were found (e.g. the API hasn't returned the new metric's data yet
    // because rawResponse still has the old metric's series), keep the previous selections
    // rather than clearing the graph. The effect will re-run once rawResponse updates.
    if (Object.keys(next).length === 0 && loading) {
      // Use the functional updater to access current state without adding it to deps
      setComparisonSelections((prev) =>
        Object.keys(prev).length > 0 ? prev : next,
      );
      return;
    }

    setComparisonSelections(next);
  }, [selectedMachines, comparisonMetric, availableComparisonMetrics, selectedMachineNode, selectedMetric, userSelectionLocked, activeComparisonOption, metrics, loading]);



  const comparisonValueFormatter = useMemo(() => {
    return (value) => {
      if (value == null || isNaN(Number(value))) return "–";

      // A costed series is labelled PKR; everything else keeps its own unit.
      const formatted = formatAmount(value);
      return effectiveMetricUnit
        ? `${formatted} ${effectiveMetricUnit}`
        : formatted;
    };
  }, [effectiveMetricUnit]);

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    if (!value) return;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return;
    setDraftRange((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleGraphDateChange = (event) => {
    const { name, value } = event.target;
    if (!value) return;

    if (mode === "minute") {
      const maxSpanMs = 24 * 60 * 60 * 1000;
      const now = new Date();

      if (name === "start") {
        const startDate = new Date(value);
        if (Number.isNaN(startDate.getTime())) return;

        const safeStart = startDate > now ? now : startDate;
        let endDate = draftRange.end;
        if (
          endDate <= safeStart ||
          endDate.getTime() - safeStart.getTime() > maxSpanMs
        ) {
          endDate = new Date(
            Math.min(safeStart.getTime() + maxSpanMs, now.getTime()),
          );
        }
        if (endDate > now) endDate = now;

        setDraftRange({ start: safeStart, end: endDate });
      } else {
        const endDate = new Date(value);
        if (Number.isNaN(endDate.getTime())) return;
        setDraftRange((prev) => ({ ...prev, end: endDate }));
      }
    } else if (mode === "second") {
      // For second mode, when start changes, set end to 1 hour later
      if (name === "start") {
        const startDate = new Date(value);
        if (Number.isNaN(startDate.getTime())) return;

        const now = new Date();
        const safeStartDate = startDate > now ? now : startDate;
        const endDate = new Date(safeStartDate.getTime() + 60 * 60 * 1000); // 1 hour later
        const safeEndDate = endDate > now ? now : endDate;

        setDraftRange({ start: safeStartDate, end: safeEndDate });
      } else {
        // If end changes, adjust to be within 1 hour of start
        const endDate = new Date(value);
        if (Number.isNaN(endDate.getTime())) return;
        setDraftRange((prev) => ({ ...prev, end: endDate }));
      }
    }
  };

  const handleMachineSelect = useCallback((node, metricOverride) => {
    // User selected a machine/node from the hierarchy
    const machineName = node?.name;
    if (!machineName) return;

    // Keep selectedMachines in sync with user's selection
    setSelectedMachines([machineName]);

    // Always clear the summary dedup cache when the selected machine changes
    // so the next fetchSummaryData call hits the API even if the machine name
    // appears to be the same as the last cached entry (e.g. switching branch
    // → machine → Usage where "Usage" was the initial cached value).
    clearSummaryCache();

    // Use the metric override when provided (e.g. called from handleApply where
    // setComparisonMetric has been called but React state hasn't updated yet).
    const effectiveMetric = metricOverride !== undefined ? metricOverride : comparisonMetric;

    // Resolve the metric option for effectiveMetric so matchers match the target metric
    const metricOption =
      comparisonMetricOptions.find(
        (opt) =>
          opt.value === effectiveMetric ||
          opt.value.toLowerCase() === String(effectiveMetric).toLowerCase(),
      ) || activeComparisonOption;

    if (machineName === "Usage") {
      setSelectedMachineNode(node);
      const effectiveUsageMetric = "power_kw";
      if (comparisonMetric !== effectiveUsageMetric) {
        setComparisonMetric(effectiveUsageMetric);
      }
      setDraftMetric(effectiveUsageMetric);
      const matchingMetric = metrics.find(
        (m) =>
          m.startsWith("Usage_") &&
          (m.toLowerCase().includes("power") || m.toLowerCase().includes("kw")),
      );
      setSelectedMetric(matchingMetric || `Usage_power_kw`);
      // Ensure API fetch requests Usage with power_kw and branch
      // NOTE: When called from handleApply, skip the internal refetch because handleApply
      // will call refetch() itself with the correct metric right after.
      if (metricOverride === undefined) {
        refetch("Usage", effectiveUsageMetric, selectedBranch);
      }
      return;
    }

    // Find the best match based on the effective Comparison Option
    const candidate = metrics.find(
      (m) =>
        (m.startsWith(`${machineName}_`) || m.startsWith(machineName)) &&
        metricOption?.matchers?.some((matcher) =>
          m.toLowerCase().includes(matcher.toLowerCase().trim()),
        ),
    );

    if (candidate) {
      setSelectedMetric(candidate);
    } else {
      setSelectedMetric(`${machineName}_${effectiveMetric}`);
    }

    // 3. Update the selected node state
    setSelectedMachineNode(node);
    // Trigger data fetch filtered to this machine (or Usage aggregate)
    // NOTE: When called from handleApply, skip the internal refetch to avoid
    // racing with the handleApply refetch which uses the correct new metric.
    if (node && node.name && metricOverride === undefined) {
      refetch(node.name === "Usage" ? "Usage" : node.name, effectiveMetric, selectedBranch);
    }
  }, [metrics, activeComparisonOption, setSelectedMetric, setSelectedMachineNode, setSelectedMachines, clearSummaryCache, comparisonMetric, refetch, selectedBranch]);

  const handleRefresh = () => {
    // Force refresh for all data hooks
    // Build selected machines param from comparison selections when available
    const selectedMetricsList = Object.keys(comparisonSelections || {}).filter((k) => comparisonSelections[k]);
    const selectedNames = [
      ...new Set(
        selectedMetricsList
          .map((m) => parseCompositeMetric(m).machineName)
          .filter(Boolean),
      ),
    ];
    const selParam = selectedNames.length ? selectedNames.join(",") : (selectedMachineNode?.name || "Usage");
    refetch(selParam, comparisonMetric, selectedBranch); // Analytics Refetch with selected machines and metric
    // Summary data is also refreshed
    const machineName = selectedMetric
      ? parseCompositeMetric(selectedMetric).machineName
      : (selectedMachineNode?.name || "Usage");
    fetchSummaryData(machineName, comparisonMetric, selectedBranch);
  };

  const pdfScopeLabel = useMemo(() => {
    const isWholeFacility =
      !selectedMachineNode?.name || selectedMachineNode.name === "Usage";
    return isWholeFacility
      ? "Whole Facility (Usage)"
      : selectedMachineNode.name;
  }, [selectedMachineNode]);

  const handleOpenPdfReportDialog = () => {
    const username = localStorage.getItem("username") || "";
    if (!username) {
      window.alert("Please log in again to download the report.");
      return;
    }
    setPdfDialogOpen(true);
  };

  const handleDownloadEnergyReport = async (reportDateRange) => {
    const username = localStorage.getItem("username") || "";
    if (!username) {
      window.alert("Please log in again to download the report.");
      return;
    }

    if (!reportDateRange?.start || !reportDateRange?.end) {
      window.alert("Select a valid date range in the dialog.");
      return;
    }

    const isWholeFacility =
      !selectedMachineNode?.name || selectedMachineNode.name === "Usage";
    const isBranchScoped = selectedBranch && selectedBranch !== "All";
    let scopeNodeName = isWholeFacility ? null : selectedMachineNode.name;
    let scopeLabel = pdfScopeLabel;

    if (isWholeFacility && isBranchScoped) {
      scopeNodeName = `Usage:${selectedBranch}`;
      scopeLabel = `Branch (${selectedBranch})`;
    }

    setPdfReportLoading(true);
    try {
      const scopeCategory = selectedMachineNode?.category || "USAGE";
      const [hierarchyData, dailySeries, heatmapData, pricing, scopeSummary] =
        await Promise.all([
          fetchEnergyHierarchy(username, reportDateRange),
          fetchDailyEnergySeries(username, reportDateRange, scopeNodeName),
          fetchHeatmapEnergyData(username, reportDateRange, scopeNodeName),
          fetchReportPricing(username),
          fetchScopeSummary(username, reportDateRange, scopeNodeName),
        ]);

      const rows = buildEnergyBreakdownRows(hierarchyData, scopeNodeName);
      const costRows = buildCostRows(rows, pricing);
      const scopeCostSummary = buildScopeCostSummary(
        scopeCategory,
        scopeSummary,
        pricing,
      );

      if (
        !rows.length &&
        !dailySeries.length &&
        !hierarchyData &&
        !heatmapData?.rows?.length
      ) {
        window.alert("No energy data found for the selected range and scope.");
        return;
      }

      await generateEnergyBreakdownPdf({
        rows,
        dailySeries,
        hierarchyData,
        scopeNodeName,
        heatmapData,
        costData: {
          costRows,
          scopeCostSummary,
        },
        metadata: {
          username,
          scopeLabel,
          start: reportDateRange.start,
          end: reportDateRange.end,
        },
      });
      setPdfDialogOpen(false);
    } catch (err) {
      console.error("Energy report PDF failed:", err);
      window.alert("Failed to generate the energy report PDF. Please try again.");
    } finally {
      setPdfReportLoading(false);
    }
  };

  const handleSelectAll = () => {
    const allNames = topLineGraphNodeOptions.map((m) => m.name).filter(Boolean);
    setSelectAllActive(true);
    setSelectedMachines(allNames);
    setUserSelectionLocked(true);
  };

  const handleSelectNone = () => {
    setSelectAllActive(false);
    const primary = selectedMachineNode?.name || draftMachine || "Usage";
    setSelectedMachines(primary ? [primary] : []);
    setComparisonSelections({});
    setUserSelectionLocked(true);
  };

  const handleApply = useCallback(() => {

    // 2. Commit date range -- only if it actually changed. Re-committing an
    // unchanged range creates a new dateRange object, and every section below
    // (Peak/Off-Peak, Energy Breakdown, Energy Flow) reloads for nothing when
    // only the machine or metric was changed.
    if (dateRangeDirty) {
      applyDateRange();
    }

    // 3. Commit metric
    const isTargetUsage = draftMachine === "Usage";
    const targetMetric = isTargetUsage ? "power_kw" : (draftMetric || comparisonMetric || "power_kw");
    if (targetMetric !== comparisonMetric) {
      setComparisonMetric(targetMetric);
    }

    // 4. Commit machine
    if (draftMachine === "__ALL_MACHINES__") {
      handleSelectAll();
      if (!dateRangeDirty) {
        refetch("ALL", targetMetric, selectedBranch);
      }
    } else {
      const selected =
        topLineGraphNodeOptions.find((n) => n.name === draftMachine) ||
        nodeOptions.find((n) => n.name === draftMachine) ||
        machinesList.find((n) => n.name === draftMachine);

      setSelectAllActive(false);
      setSelectedMachines([draftMachine]);
      setUserSelectionLocked(true);
      if (selected) {
        handleMachineSelect(selected, targetMetric);
      }
      if (!dateRangeDirty) {
        refetch(draftMachine || "Usage", targetMetric, selectedBranch);
      }
    }

  }, [
    applyDateRange,
    dateRangeDirty,
    draftMetric,
    comparisonMetric,
    draftMachine,
    nodeOptions,
    machinesList,
    handleSelectAll,
    handleMachineSelect,
    refetch,
    topLineGraphNodeOptions,
    selectedBranch,
  ]);

  // 4. Default selection logic based on branch and category
  useEffect(() => {
    if (topLineGraphNodeOptions.length > 0 && !selectedMachineNode) {
      if (userSelectionLocked) return;

      // Always default to "Usage" (total view) regardless of whether a branch
      // is selected. This mirrors the "All Branches" experience where users
      // see the aggregate usage graph first.
      const getPriorityMatch = (cat) =>
        topLineGraphNodeOptions.find((m) => (m.category || "").toUpperCase() === cat);

      const usageNode = topLineGraphNodeOptions.find((m) => m.name === "Usage");
      const firstGrid = getPriorityMatch("GRID");
      const firstMachine = getPriorityMatch("MACHINE");

      const targetDefault = usageNode || firstGrid || firstMachine || topLineGraphNodeOptions[0];
      if (targetDefault) {
        setSelectedMachines([targetDefault.name]);
        handleMachineSelect(targetDefault);
      }

      setUserSelectionLocked(true);
    }
  }, [
    machinesList,
    selectedMachineNode,
    selectedBranch,
    nodesUnderBranch,
    userSelectionLocked,
    comparisonMetric,
    handleMachineSelect,
    refetch,
    topLineGraphNodeOptions,
  ]);

  // Sync state when selectedBranch changes
  const prevBranchRef = useRef(selectedBranch);
  useEffect(() => {
    if (prevBranchRef.current !== selectedBranch) {
      prevBranchRef.current = selectedBranch;
      clearFetchCache();
      clearSummaryCache();
      setSummaryData(null);

      if (selectedBranch && selectedBranch !== "All") {
        setSelectAllActive(false);
        const branchUsageNode = nodeOptions.find((n) => n.name === "Usage") || {
          name: "Usage",
          displayName: `${selectedBranch} (Usage)`,
          category: "USAGE",
          branch: selectedBranch,
        };
        setSelectedMachines(["Usage"]);
        setDraftMachine("Usage");
        setDraftMetric("power_kw");
        setComparisonMetric("power_kw");
        handleMachineSelect(branchUsageNode, "power_kw");
      } else {
        // "All" branches selected, default to the "Usage" aggregate
        setSelectAllActive(false);
        const usageNode = machinesList.find((m) => m.name === "Usage");
        if (usageNode) {
          setSelectedMachines(["Usage"]);
          setDraftMachine("Usage");
          setDraftMetric("power_kw");
          setComparisonMetric("power_kw");
          handleMachineSelect(usageNode, "power_kw");
        }
      }
    }
  }, [selectedBranch, nodeOptions, machinesList, handleMachineSelect, clearFetchCache, clearSummaryCache, setSummaryData]);

  const heatmapMachineColor = useMemo(() => {
    const name = selectedMachineNode?.name;
    let color = name ? machineColors?.[name] : undefined;
    if (!color || color === "NA" || color === "na") color = "#ef4444";
    return color;
  }, [selectedMachineNode?.name, machineColors]);

  const heatmapValueFormatter = useCallback((value) => {
    const unit =
      pkrMultiplier !== 1
        ? "PKR"
        : getUnitForMetric(selectedMetric);
    return value == null
      ? "–"
      : unit === "pf"
        ? value.toFixed(3)
        : `${Number(value).toFixed(1)} ${unit}`;
  }, [pkrMultiplier, selectedMetric]);
  return (
    <div className="historical-container">
      <EnergyReportPdfDialog
        isOpen={pdfDialogOpen}
        onClose={() => {
          if (!pdfReportLoading) setPdfDialogOpen(false);
        }}
        onGenerate={handleDownloadEnergyReport}
        loading={pdfReportLoading}
        scopeLabel={pdfScopeLabel}
        initialDateRange={stableDateRange || dateRange}
      />
      <div className="pdf-report-tab-wrap">
        <button
          type="button"
          onClick={handleOpenPdfReportDialog}
          className="pdf-report-tab"
          disabled={pdfReportLoading}
          aria-label="Open energy report PDF dialog"
        >
          <span className="pdf-report-tab-panel">
            <span className="pdf-report-tab-title">
              {pdfReportLoading ? (
                "Generating…"
              ) : (
                <FieldHelp id="historical.pdfReport">Energy Report</FieldHelp>
              )}
            </span>
            <span className="pdf-report-tab-subtitle">
              {pdfReportLoading
                ? "Please wait"
                : "Select dates and download PDF"}
            </span>
          </span>
          <span className="pdf-report-tab-rail" aria-hidden="true">
            {pdfReportLoading ? (
              <span className="pdf-report-tab-spinner" />
            ) : (
              <svg
                className="pdf-report-tab-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 2H8C6.9 2 6 2.9 6 4V20C6 21.1 6.9 22 8 22H16C17.1 22 18 21.1 18 20V8L14 2Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 2V8H18M9 13H15M9 17H13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span className="pdf-report-tab-label">PDF Report</span>
          </span>
        </button>
      </div>
      {/* Full-screen overlay removed — tab opens immediately; each section has its own inline loader */}
      {/* 1. Header & Controls Section */}
      <section className="control-panel">
        <div className="control-row">
          {/* Unified Filter Group in Single Line */}
          <div className="unified-filter-wrapper">
            {/* Date/Time Pickers */}
            <div className="date-input-wrapper">
              {mode === "second" && (
                <label className="hour-picker-label">
                  <FieldHelp id="historical.dateRange">Hour:</FieldHelp>
                </label>
              )}
              <input
                type="datetime-local"
                name="start"
                value={formatDateTimeLocal(draftRange.start)}
                onChange={mode === "second" || mode === "minute" ? handleGraphDateChange : handleDateChange}
                onClick={(e) => e.target.showPicker?.()}
                max={formatDateTimeLocal(new Date())}
                step={mode === "minute" ? "60" : "1"}
                className="clean-date-input"
              />
              <span className="date-separator">
                <FieldHelp id="historical.dateRange">TO</FieldHelp>
              </span>
              <input
                type="datetime-local"
                name="end"
                value={formatDateTimeLocal(draftRange.end)}
                onChange={mode === "second" || mode === "minute" ? handleGraphDateChange : handleDateChange}
                onClick={(e) => e.target.showPicker?.()}
                max={formatDateTimeLocal(new Date())}
                step={mode === "minute" ? "60" : "1"}
                className="clean-date-input"
              />
            </div>

            {/* Quick Ranges */}
            {mode !== "second" && mode !== "minute" && (
              <div className="quick-ranges-group">
                <FieldHelp id="historical.quickRange" style={{ display: "contents" }}>
                  {quickRanges.map((range) => (
                    <button
                      key={range.value}
                      type="button"
                      onClick={() => {
                        const rel = getRelativeRange(range.value);
                        setDraftRange(rel);
                      }}
                      className="mini-chip"
                    >
                      {range.label}
                    </button>
                  ))}
                </FieldHelp>
              </div>
            )}

            {/* Machine Selection Dropdown */}
            <div className="filter-dropdown-group">
              <FieldHelp id="historical.machineSelect">
                <select
                  value={draftMachine}
                  onChange={(e) => {
                    const newMachine = e.target.value;
                    setDraftMachine(newMachine);
                    if (newMachine === "Usage") {
                      setDraftMetric("power_kw");
                    }
                  }}
                  className="clean-select text-ellipsis machine-select"
                  title={draftMachine === "__ALL_MACHINES__" ? "All Machines" : draftMachine}
                >
                  {selectedBranch !== "All" && (
                    <option value="__ALL_MACHINES__">All Machines</option>
                  )}
                  {topLineGraphNodeOptions.map((node) => (
                    <option key={node.name} value={node.name}>
                      {node.name === "Usage"
                        ? (selectedBranch && selectedBranch !== "All" ? `${selectedBranch} Usage (Branch Total)` : "Usage (Total Consumption)")
                        : node.name}
                      {node.is_branch
                        ? " (Branch)"
                        : node.category && node.category !== "Unknown" && node.name !== "Usage"
                          ? ` (${node.category})`
                          : ""}
                    </option>
                  ))}
                </select>
              </FieldHelp>
            </div>

            {/* Metric Selection Dropdown: hidden when Usage is selected */}
            {draftMachine !== "Usage" && (
              <div className="filter-dropdown-group">
                <FieldHelp id="historical.metricSelect">
                  <select
                    value={draftMetric}
                    onChange={(e) => setDraftMetric(e.target.value)}
                    className="clean-select metric-select"
                  >
                    {comparisonMetricOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FieldHelp>
              </div>
            )}

            {/* Apply Button */}
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              title={
                isValidRange(draftRange)
                  ? "Apply filters and load data"
                  : "Start date must be on or before the end date"
              }
              className={`apply-filter-btn ${canApply ? "can-apply" : ""}`}
            >
              Apply
            </button>
          </div>

          {/* Right Action Controls: Refresh & Currency Mode */}
          <div className="filter-actions-right">
            {/* Currency Toggle */}
            <div className="currency-toggle-wrapper">
              <FieldHelp id="historical.currencyMode" style={{ display: "contents" }}>
                {["Energy", "PKR"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setCurrencyMode(mode)}
                    className={`currency-toggle-btn ${currencyMode === mode ? "active" : ""}`}
                    style={{
                      backgroundColor:
                        currencyMode === mode
                          ? mode === "PKR"
                            ? "#1BA08E"
                            : "#3b82f6"
                          : "transparent",
                    }}
                  >
                    {mode}
                  </button>
                ))}
              </FieldHelp>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Stats Section - Responsive Tables */}

      {/* Summary Cards Row (Moved above tables) */}
      <section className="summary-cards-section">
        <div className="summary-cards-wrapper">
          {(() => {
            const isPKR = currencyMode === "PKR";
            const currentMachineName = selectedMachineNode?.name || draftMachine || "Usage";
            const category = (selectedMachineNode?.category || (currentMachineName === "Usage" ? "USAGE" : "")).toUpperCase();
            const brandColors = {
              teal: "#009f8d",
              orange: "#ff9c07",
              deepTeal: "#005051",
              navy: "#002f46",
              lightTeal: "#00c6a9",
            };

            const metricCardInfo = {
              power_kw: { label: "POWER", unit: "kW" },
              energy: { label: "ENERGY", unit: "kWh" },
              voltage: { label: "VOLTAGE", unit: "V" },
              power_factor: { label: "POWER FACTOR", unit: "" },
              current: { label: "CURRENT", unit: "A" },
              frequency: { label: "FREQUENCY", unit: "Hz" },
              THD_V: { label: "THD VOLTAGE", unit: "%" },
              THD_I: { label: "THD CURRENT", unit: "%" },
              TAP: { label: "TAP", unit: "kW" },
            };
            const currentMetricInfo = metricCardInfo[comparisonMetric] || { label: "POWER", unit: "kW" };

            // Check if this is a Grid or Usage category for special energy display
            const isGridOrUsage = category === "GRID" || category === "USAGE" || currentMachineName === "Usage";

            // Robust Energy calculations from backend summary
            const importedKwh = Number(summaryData?.EnergyConsumed) || 0;
            const exportedKwh = Number(summaryData?.EnergyGenerated) || 0;

            let netEnergyValue = 0;
            if (summaryData?.NetEnergy !== undefined && summaryData?.NetEnergy !== null && summaryData?.NetEnergy !== 0) {
              netEnergyValue = summaryData.NetEnergy;
            } else if (summaryData?.EnergyConsumed !== undefined || summaryData?.EnergyGenerated !== undefined) {
              netEnergyValue = importedKwh - exportedKwh;
            } else if (comparisonMetric === "energy" && stats?.average !== undefined && stats?.average !== null) {
              netEnergyValue = stats.average;
            }

            let generatorEnergyValue = 0;
            if (summaryData?.EnergyGenerated !== undefined && summaryData?.EnergyGenerated !== null && summaryData?.EnergyGenerated !== 0) {
              generatorEnergyValue = summaryData.EnergyGenerated;
            } else if (exportedKwh > 0) {
              generatorEnergyValue = exportedKwh;
            } else if (comparisonMetric === "energy" && stats?.average !== undefined && stats?.average !== null) {
              generatorEnergyValue = stats.average;
            }

            // Cost calculation logic
            let totalCost = 0;
            let estimatedCost = 0;
            if (category === "GENERATOR") {
              totalCost = generatorEnergyValue * (isPKR ? unitPrice : 1);
              estimatedCost = generatorEnergyValue * unitPrice;
            } else {
              totalCost = netEnergyValue * (isPKR ? unitPrice : 1);
              estimatedCost = netEnergyValue * unitPrice;
            }
            const unit = isPKR ? "PKR" : "kWh";
            const cards = [];

            // Calculate average energy consumption per day
            const startMs = graphDateRange.start ? graphDateRange.start.getTime() : (dateRange.start ? dateRange.start.getTime() : Date.now());
            const endMs = graphDateRange.end ? graphDateRange.end.getTime() : (dateRange.end ? dateRange.end.getTime() : Date.now());
            const diffDays = Math.max(1, Math.round(Math.abs(endMs - startMs) / (1000 * 60 * 60 * 24)));
            const avgDailyEnergy = netEnergyValue / diffDays;
            const avgDailyCost = (isPKR
              ? (importedKwh * tariffs.grid - exportedKwh * tariffs.exportCredit)
              : (netEnergyValue * unitPrice)) / diffDays;

            // First card changes based on category
            if (isGridOrUsage) {
              // Special card for Grid/Usage categories showing Net Energy with Import/Export
              // Import and export carry different rates, so the PKR net is
              // (import cost - export credit), not NetEnergy x one rate.
              cards.push({
                title: isPKR ? "Net Cost" : "Net Energy",
                helpId: isPKR ? "historical.netCost" : "historical.netEnergy",
                value: isPKR
                  ? importedKwh * tariffs.grid - exportedKwh * tariffs.exportCredit
                  : netEnergyValue,
                color: brandColors.teal,
                isEnergyCard: true,
                import: isPKR ? importedKwh * tariffs.grid : importedKwh,
                export: isPKR ? exportedKwh * tariffs.exportCredit : exportedKwh,
                avgDailyText: `${formatAmount(isPKR ? avgDailyCost : avgDailyEnergy)} ${isPKR ? "PKR" : "kWh"}/day`,
                // Lightning Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      fill={brandColors.teal}
                      stroke={brandColors.teal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              });
            } else {
              // Default card for other categories
              cards.push({
                title: isPKR ? "Total Cost" : mainEnergyLabel,
                helpId: isPKR
                  ? "historical.totalCost"
                  : mainEnergyLabel === "Generation"
                    ? "historical.generation"
                    : mainEnergyLabel === "Consumption"
                      ? "historical.consumption"
                      : "historical.netEnergy",
                value: totalCost,
                color: brandColors.teal,
                avgDailyText: `${formatAmount(isPKR ? avgDailyCost : avgDailyEnergy)} ${isPKR ? "PKR" : "kWh"}/day`,
                // Lightning Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      fill={brandColors.teal}
                      stroke={brandColors.teal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              });
            }
            const isUsageOnly = currentMachineName === "Usage" || category === "USAGE";
            const minSummaryVal = typeof summaryData?.Consumed?.min === "object" ? summaryData?.Consumed?.min?.value : summaryData?.Consumed?.min;
            const minSummaryTs = typeof summaryData?.Consumed?.min === "object" ? summaryData?.Consumed?.min?.timestamp : null;
            const maxSummaryVal = typeof summaryData?.Consumed?.max === "object" ? summaryData?.Consumed?.max?.value : summaryData?.Consumed?.max;
            const maxSummaryTs = typeof summaryData?.Consumed?.max === "object" ? summaryData?.Consumed?.max?.timestamp : null;

            // Check if stats from timeseries graph currently matches comparisonMetric
            const statsMatchCurrentMetric = Boolean(
              stats &&
              selectedMetric &&
              activeComparisonOption?.matchers?.some((matcher) =>
                selectedMetric.toLowerCase().includes(matcher.toLowerCase().trim()),
              )
            );

            // Check if summaryData from API matches comparisonMetric
            const summaryMatchesMetric = Boolean(
              summaryData?.Metric &&
              (summaryData.Metric.toLowerCase() === comparisonMetric.toLowerCase() ||
                activeComparisonOption?.matchers?.some((m) =>
                  summaryData.Metric.toLowerCase().includes(m.toLowerCase().trim()),
                ))
            );

            const effectiveAvg = statsMatchCurrentMetric
              ? (isUsageOnly
                  ? (stats?.average ?? (summaryMatchesMetric ? summaryData?.Consumed?.avg : 0))
                  : (summaryMatchesMetric && summaryData?.Consumed?.avg !== undefined ? summaryData.Consumed.avg : stats?.average))
              : (summaryMatchesMetric && summaryData?.Consumed?.avg !== undefined ? summaryData.Consumed.avg : 0);

            const effectiveMin = statsMatchCurrentMetric
              ? (isUsageOnly
                  ? (stats?.min ?? (summaryMatchesMetric ? minSummaryVal : 0))
                  : (summaryMatchesMetric && minSummaryVal !== undefined ? minSummaryVal : stats?.min))
              : (summaryMatchesMetric && minSummaryVal !== undefined ? minSummaryVal : 0);

            const effectiveMinTs = statsMatchCurrentMetric
              ? (isUsageOnly
                  ? (stats?.minTimestamp || (summaryMatchesMetric ? minSummaryTs : null))
                  : (summaryMatchesMetric && minSummaryTs ? minSummaryTs : stats?.minTimestamp))
              : (summaryMatchesMetric && minSummaryTs ? minSummaryTs : null);

            const effectiveMax = statsMatchCurrentMetric
              ? (isUsageOnly
                  ? (stats?.max ?? (summaryMatchesMetric ? maxSummaryVal : 0))
                  : (summaryMatchesMetric && maxSummaryVal !== undefined ? maxSummaryVal : stats?.max))
              : (summaryMatchesMetric && maxSummaryVal !== undefined ? maxSummaryVal : 0);

            const effectiveMaxTs = statsMatchCurrentMetric
              ? (isUsageOnly
                  ? (stats?.maxTimestamp || (summaryMatchesMetric ? maxSummaryTs : null))
                  : (summaryMatchesMetric && maxSummaryTs ? maxSummaryTs : stats?.maxTimestamp))
              : (summaryMatchesMetric && maxSummaryTs ? maxSummaryTs : null);

            // Always add remaining cards regardless of category
            cards.push(
              {
                title: "Estimated Cost",
                helpId: "historical.estimatedCost",
                value: estimatedCost,
                color: brandColors.orange,
                unit: "PKR",
                // Coins Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke={brandColors.orange}
                      strokeWidth="2"
                    />
                    <path
                      d="M11 13C11 13 14.5 14 14.5 17.5C14.5 21 11 22 11 22M11 13V22M11 13C11 13 7.5 12 7.5 8.5C7.5 5 11 4 11 4M11 13V4"
                      stroke={brandColors.orange}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="16"
                      cy="16"
                      r="7"
                      fill={brandColors.orange}
                      stroke={brandColors.orange}
                      strokeWidth="2"
                    />
                  </svg>
                ),
              },
              {
                title: `AVG ${currentMetricInfo.label}`,
                helpId: "historical.avgMetric",
                value: effectiveAvg ?? 0,
                color: "#8e24aa", // Medium Purple
                unit: currentMetricInfo.unit,
                // Gauge Icon
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 12H4M20 12H22M4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12M12 12V8"
                      stroke="#8e24aa"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
              {
                title: `MIN ${currentMetricInfo.label}`,
                helpId: "historical.minMetric",
                value: effectiveMin ?? 0,
                timestamp: effectiveMinTs,
                color: "#1e88e5", // Medium Blue
                unit: currentMetricInfo.unit,
                // Graph Down
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L10 12L14 8L20 18"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 18H16"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M20 18V14"
                      stroke="#1e88e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
              {
                title: `MAX ${currentMetricInfo.label}`,
                helpId: "historical.maxMetric",
                value: effectiveMax ?? 0,
                timestamp: effectiveMaxTs,
                color: brandColors.lightTeal,
                unit: currentMetricInfo.unit,
                // Graph Up
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 18L10 12L14 16L20 6"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 6H16"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M20 6V10"
                      stroke={brandColors.lightTeal}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              },
            );

            const isUsage = !currentMachineName || currentMachineName === "Usage" || currentMachineName === "__ALL_MACHINES__";

            // Find machine-specific MDI if a separate machine or separate grid is selected
            const selectedMachineMdi =
              !isUsage
                ? (
                  mdiData?.machine_mdi?.find(
                    (m) =>
                      m.machine_name === currentMachineName ||
                      (selectedMachineNode?.meter_id &&
                        m.meter_id === selectedMachineNode.meter_id)
                  ) ||
                  machinesMdiData?.machine_mdi?.find(
                    (m) =>
                      m.machine_name === currentMachineName ||
                      (selectedMachineNode?.meter_id &&
                        m.meter_id === selectedMachineNode.meter_id)
                  )
                )
                : null;

            // When Usage is selected, directly use /api/mdi (mdiData)
            const mdiValueInKW = Number(
              selectedMachineMdi
                ? (selectedMachineMdi.reference_kw ?? selectedMachineMdi.mdi_value ?? 0)
                : (mdiData?.mdi_reference_kw ?? mdiData?.mdi_value ?? mdiData?.peak_power_kw ?? machinesMdiData?.mdi_reference_kw ?? 0)
            );
            const mdiValueInPKR = Number(
              selectedMachineMdi
                ? (selectedMachineMdi.mdi_value ?? selectedMachineMdi.reference_kw ?? 0)
                : (mdiData?.mdi_value ?? mdiData?.mdi_reference_kw ?? mdiValueInKW)
            );
            const parsedAvgPf = Number(avgMonthlyPfData?.avg_power_factor);
            const avgPfRatio = Number.isFinite(parsedAvgPf) ? parsedAvgPf : null;
            // Green if >= 0.9, Red if < 0.9, Amber if no data
            const avgPfColor = avgPfRatio === null ? "#d97706" : avgPfRatio >= 0.9 ? "#22c55e" : "#ef4444";

            cards.push({
              title: "MDI",
              helpId: "historical.mdi",
              // MDI is worked out by the API from minute data (minute_wise_data,
              // mdi.py), not from the chart's buckets. Say so, or in Hour/Day
              // mode it looks like it came from the hourly chart.
              note: "30-min sustained peak, from minute data",
              value: isPKR ? mdiValueInPKR : mdiValueInKW,
              color: "#e11d48", // Rose Red
              unit: isPKR ? "PKR" : "kW",
              timestamp:
                selectedMachineMdi
                  ? (selectedMachineMdi.mdi_timestamp ?? selectedMachineMdi.timestamp ?? null)
                  : (mdiData?.mdi_timestamp ?? mdiData?.timestamp ?? machinesMdiData?.mdi_timestamp ?? null),
              tooltip: "Maximum Demand Indicator: the highest load (kW) held continuously for a full 30 minutes. Short spikes don't count.",
              icon: (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 20V10M12 20V4M6 20V14"
                    stroke="#e11d48"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 4h20"
                    stroke="#e11d48"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="4 4"
                  />
                  <circle cx="12" cy="4" r="2" fill="#e11d48" />
                </svg>
              ),
            });

            const isGridMachineNode = (selectedMachineNode?.category || "").toUpperCase() === "GRID";
            if (isGridMachineNode) {
              cards.push({
                title: "AVG Monthly PF",
                helpId: "historical.avgMonthlyPf",
                value: avgPfRatio,
                color: avgPfColor,
                unit: "",
                isAvgPfCard: true,
                icon: (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22Z"
                      stroke={avgPfColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 12h4M12 8v4"
                      stroke={avgPfColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 15l2 2"
                      stroke={avgPfColor}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ),
              });
            }

            return cards.map((card, idx) => (
              <div
                key={idx}
                className={`summary-card ${card.isEnergyCard ? 'energy-card' : ''}`}
                style={{
                  backgroundColor: `${card.color}15`,
                  border: `1px solid ${card.color}30`,
                  ...(card.isEnergyCard && { minheight: 150, flex: '0 0 auto' })
                }}
              >
                {/* Shimmer skeleton while data is loading */}
                {loading || summaryLoading ? (
                  <>
                    <div className="summary-card-shimmer-line summary-card-shimmer-title" />
                    <div className="summary-card-shimmer-line summary-card-shimmer-value" />
                    <div className="summary-card-shimmer-line summary-card-shimmer-sub" />
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        className="summary-card-title"
                        style={{ color: card.color, opacity: 0.9 }}
                      >
                        <FieldHelp id={card.helpId}>{card.title}</FieldHelp>
                      </div>
                      <div style={{ opacity: 1 }}>{card.icon}</div>
                    </div>
                    <div
                      className="summary-card-value"
                      style={{ color: card.color }}
                    >
                      {card.isEnergyCard ? (
                        <div>
                          {/* Line 1: Main Value */}
                          <div style={{ fontSize: "28px", fontWeight: "800", color: card.color }}>
                            {formatAmount(card.value)}
                            <span style={{ fontSize: "17px", fontWeight: "600", color: card.color, marginLeft: "6px" }}>
                              {unit}
                            </span>
                          </div>

                          {/* Line 2: Import & Export Values */}
                          <div style={{ display: "flex", marginTop: "8px" }}>
                            <span style={{ fontSize: "12px", fontWeight: "700", flex: "1", color: "#e11d48" }}>
                              {formatAmount(card.import)} <span style={{ fontSize: "9px", fontWeight: "500", color: "#e11d48" }}>{unit}</span>
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: "700", color: card.color }}>
                              {formatAmount(card.export)} <span style={{ fontSize: "9px", fontWeight: "500", color: card.color }}>{unit}</span>
                            </span>
                          </div>

                          {/* Line 3: Labels */}
                          <div style={{ display: "flex", gap: "12px", fontSize: "12px", fontWeight: "600", color: "#6b7280" }}>
                            <span style={{ color: "#e11d48", flex: "1" }}>
                              <FieldHelp id="historical.importEnergy">Import</FieldHelp>
                            </span>
                            <span style={{ color: card.color }}>
                              <FieldHelp id="historical.exportEnergy">Export</FieldHelp>
                            </span>
                          </div>

                          {/* Line 4: Average Daily Consumption */}
                          {card.avgDailyText && (
                            <div style={{ marginTop: "6px", fontSize: "11px", fontWeight: "600", color: "#64748b" }}>
                              Avg: {card.avgDailyText}
                            </div>
                          )}
                        </div>
                      ) : card.isAvgPfCard ? (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {avgMonthlyPfLoading ? (
                            <span
                              className="spinner"
                              style={{
                                borderTopColor: card.color,
                                width: 20,
                                height: 20,
                                borderWidth: 3,
                              }}
                            ></span>
                          ) : card.value === null ? (
                            <div>
                              <span style={{ fontSize: "22px", fontWeight: "700", color: "#9ca3af" }}>N/A</span>
                              <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 500, marginTop: "4px" }}>No data (last 30 days)</div>
                            </div>
                          ) : (
                            <div>
                              <span style={{ fontSize: "26px", fontWeight: "800", color: card.color }}>
                                {Number(card.value).toFixed(2)}
                              </span>
                              <div style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: `${card.color}bb`,
                                marginTop: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                              }}>
                                <span style={{
                                  display: "inline-block",
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  background: card.color,
                                  flexShrink: 0
                                }}></span>
                                {"Last 30 days"}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div>
                            {Number(card.value || 0).toFixed(2)}
                            <span
                              className="summary-card-unit"
                              style={{ color: card.color, opacity: 0.8 }}
                            >
                              {card.unit ?? unit}
                            </span>
                          </div>
                          {card.timestamp && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: `${card.color}99`,
                                fontWeight: 600,
                                marginTop: "2px",
                              }}
                            >
                              {(() => {
                                try {
                                  const tsStr = String(card.timestamp);
                                  const parsed = new Date(tsStr.replace(" ", "T"));
                                  return isNaN(parsed.getTime())
                                    ? tsStr
                                    : parsed.toLocaleString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    });
                                } catch {
                                  return String(card.timestamp);
                                }
                              })()}
                            </div>
                          )}
                          {card.note && (
                            <div
                              style={{
                                fontSize: "10px",
                                color: `${card.color}99`,
                                fontWeight: 500,
                                marginTop: "2px",
                              }}
                            >
                              {card.note}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ));
          })()}
        </div>
      </section>

      {/* 3. Charts & Analytics Section */}
      <section
        className="analytics-layout-container"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <div
          className="analytics-main-card"
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Enhanced Header */}
          <div
            style={{
              padding: "16px 20px 0 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* Redundant title removed as the metric selector is inline */}

              {/* Select All/None Actions */}
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  onClick={handleSelectAll}
                  style={{
                    fontSize: "9px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#475569",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.background = "#fff")}
                  onMouseLeave={(e) => (e.target.style.background = "#f8fafc")}
                  type="button"
                >
                  <FieldHelp id="historical.selectAllNone">ALL</FieldHelp>
                </button>
                <button
                  onClick={handleSelectNone}
                  style={{
                    fontSize: "9px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    background: "#f8fafc",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#475569",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.background = "#fff")}
                  onMouseLeave={(e) => (e.target.style.background = "#f8fafc")}
                  type="button"
                >
                  <FieldHelp id="historical.selectAllNone">NONE</FieldHelp>
                </button>
              </div>
            </div>

            {/* Phase-wise Selector (Overall, 1st Phase, 2nd Phase, 3rd Phase, All Phases) */}
            {selectedMachines && selectedMachines.length === 1 && selectedMachineNode && selectedMachineNode.phase_type !== 1 && selectedMachineNode.phase_type !== "1" && selectedMachineNode.name !== "Usage" && viewMode === "chart" ? (
              <div
                style={{
                  display: "flex",
                  background: "#f1f5f9",
                  padding: 2,
                  borderRadius: 8,
                  gap: 2,
                }}
              >
                <FieldHelp id="historical.phaseMode" style={{ display: "contents" }}>
                  {[
                    { id: "overall", label: "Overall" },
                    { id: "phase1", label: "1st Phase" },
                    { id: "phase2", label: "2nd Phase" },
                    { id: "phase3", label: "3rd Phase" },
                    { id: "all", label: "All Phases" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPhaseMode(p.id)}
                      type="button"
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: selectedPhaseMode === p.id ? "#fff" : "transparent",
                        color: selectedPhaseMode === p.id ? "#0f172a" : "#64748b",
                        fontSize: "10px",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: selectedPhaseMode === p.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </FieldHelp>
              </div>
            ) : null}

            {/* Grid-level Energy flow selector (total / import / export) */}
            {showGridEnergyFlow && viewMode === "chart" && (
              <div
                style={{
                  display: "flex",
                  background: "#f1f5f9",
                  padding: 2,
                  borderRadius: 8,
                }}
              >
                <FieldHelp id="historical.gridEnergyFlow">
                  <select
                    className="flow-select"
                    value={gridEnergyFlow}
                    onChange={(e) => setGridEnergyFlow(e.target.value)}
                  >
                    <option value="total">⚡ TOTAL</option>
                    <option value="import">↓ IMPORT</option>
                    <option value="export">↑ EXPORT</option>
                  </select>
                </FieldHelp>
              </div>
            )}

            {/* View Mode Toggle (Chart vs Heatmap) */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              {[
                { id: "chart", label: "ANALYSIS", icon: "📈", helpId: "historical.analysisView" },
                { id: "heatmap", label: "HEATMAP", icon: "🌡️", helpId: "historical.heatmapView" },
              ].map((v) => (
                <button
                  key={v.id}
                  onClick={() => setViewMode(v.id)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "none",
                    backgroundColor: viewMode === v.id ? "#fff" : "transparent",
                    color: viewMode === v.id ? "#0f172a" : "#64748b",
                    fontSize: "10px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow:
                      viewMode === v.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <span>{v.icon}</span>
                  <FieldHelp id={v.helpId}>{v.label}</FieldHelp>
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexShrink: 0,
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              <FieldHelp id="historical.resolution" style={{ display: "contents" }}>
                {["second", "minute", "hour", "day"].map((m) => {
                  const isDisabled =
                    (m === "minute" || m === "second") && graphType === "bar";
                  return (
                    <button
                      key={m}
                      onClick={() => !isDisabled && setMode(m)}
                      disabled={isDisabled}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: mode === m ? "#fff" : "transparent",
                        color: mode === m ? "#0f172a" : "#64748b",
                        fontSize: "10px",
                        fontWeight: 700,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.4 : 1,
                        boxShadow:
                          mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      }}
                    >
                      {m.toUpperCase()}
                    </button>
                  );
                })}
              </FieldHelp>
            </div>

            {/* Graph Type Toggle */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 2,
                borderRadius: 8,
              }}
            >
              {["line", "bar"].map((t) => {
                const isDisabled =
                  (t === "bar" && (mode === "minute" || mode === "second")) ||
                  (t === "line" && isEnergyMetric);
                return (
                  <button
                    key={t}
                    onClick={() => !isDisabled && setGraphType(t)}
                    disabled={isDisabled}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor:
                        graphType === t ? "#fff" : "transparent",
                      color: graphType === t ? "#0f172a" : "#64748b",
                      fontSize: "10px",
                      fontWeight: 700,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.4 : 1,
                      boxShadow:
                        graphType === t
                          ? "0 1px 3px rgba(0,0,0,0.1)"
                          : "none",
                    }}
                  >
                    <FieldHelp id="historical.graphType">{t.toUpperCase()}</FieldHelp>
                  </button>
                );
              })}
            </div>

            {/* AI Analyzer Button & Burger Menu for Export */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, borderLeft: "1px solid #e2e8f0", paddingLeft: 8 }}>
              <AIAnalyzerButton onClick={handleAnalyzeMainChart} />
              <ChartBurgerMenu
                chartTitle={`${activeComparisonOption.label}_Analytics`}
                chartRef={comparisonChartRef}
                exportContainer={true}
                onAIAnalyze={handleAnalyzeMainChart}
                chartData={{
                  labels: comparisonSeries.labels,
                  datasets: comparisonSeries.datasets.map((d) => ({
                    label: d.label,
                    data: d.data,
                  })),
                  unit: effectiveMetricUnit,
                }}
                chartType={viewMode === "chart" ? graphType : "heatmap"}
              />
            </div>
          </div>
          <div
            className="comparison-chart-container"
            ref={comparisonChartRef}
            style={{
              padding: "0 20px",
              minHeight: "500px",
              flex: 1,
              background: "#fff",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Lightning bolt loader inside the chart container, same visual as old full-screen but scoped */}
            {viewMode === "chart" && comparisonGraphLoading && (
              <BoltLoader
                progress={loadingProgress}
                label={
                  mode === "second"
                    ? "Loading second-wise data…"
                    : "Loading historical data…"
                }
              />
            )}

            <div style={{ flex: 1, minHeight: 400, position: 'relative' }}>
              {viewMode === "chart" ? (
                comparisonGraphLoading ? null : comparisonGraphError ? (
                  <ChartStatus
                    type="error"
                    title="Graph data could not be loaded"
                    message={comparisonGraphError}
                    actionLabel="Retry"
                    onAction={handleRefresh}
                  />
                ) : hasComparisonChartData ? (
                  <ErrorBoundary>
                    <ApexGraph
                      id="comparison-analytics-chart"
                      labels={comparisonSeries.labels}
                      rawLabels={comparisonSeries.rawLabels}
                      datasets={comparisonSeries.datasets}
                      statLines={comparisonStatLines}
                      chartType={graphType}
                      valueFormatter={comparisonValueFormatter}
                      showBrush={true}
                      mode={mode}
                      metricUnit={effectiveMetricUnit}
                    />
                  </ErrorBoundary>
                ) : (
                  <ChartStatus
                    type="empty"
                    title={
                      availableComparisonMetrics.length === 0
                        ? "No data available in this range"
                        : "Select metrics below to view chart"
                    }
                    message={
                      availableComparisonMetrics.length === 0
                        ? "Try a different date range or machine."
                        : "Choose one or more metrics to draw the graph."
                    }
                  />
                )
              ) : (
                <div className="heatmap-wrapper" style={{ height: "100%", border: "none", boxShadow: "none", marginTop: 0 }}>
                  <HeatmapChart
                    rows={heatmapRows}
                    timeLabels={heatmapTimeLabels}
                    minValue={heatmapMin}
                    maxValue={heatmapMax}
                    loading={heatmapLoading}
                    machineColor={heatmapMachineColor}
                    metricLabel={selectedMachineNode?.name || ""}
                    titleHelpId="historical.heatmapView"
                    valueFormatter={heatmapValueFormatter}
                  />
                  {!hasHeatmapData && !heatmapLoading && (
                    <div
                      style={{
                        padding: 40,
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: 14,
                      }}
                    >
                      No heatmap insights available for this period.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Machine selection badges moved inside the card - only show in Analysis mode */}
            {viewMode === "chart" && (
              <div style={{ width: "100%", padding: "16px 0 20px 0", borderTop: '1px solid #f1f5f9', backgroundColor: '#fff', zIndex: 5 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ color: "#64748b", fontSize: 12, fontWeight: 600 }}><FieldHelp id="historical.selectedMachines">Selected Machines</FieldHelp>: {selectedMachines.length}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
                  <button
                    onClick={() => scrollMachineList("left")}
                    className="scroll-arrow-button"
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      color: "#64748b",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                    type="button"
                    aria-label="Scroll left"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <div
                    ref={machineListRef}
                    className="no-scrollbar"
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      padding: "4px 0",
                      flex: 1,
                      scrollBehavior: "smooth",
                    }}
                  >
                    {topLineGraphNodeOptions.map((node) => {
                      const primaryMachine = selectedMachineNode?.name || draftMachine || "Usage";
                      const isPrimary = node.name === primaryMachine;
                      const isActive = selectedMachines.includes(node.name) || isPrimary;
                      const color = machineColors[node.name] || "#64748b";
                      return (
                        <button
                          key={node.name}
                          onClick={() => {
                            if (isPrimary) {
                              // Primary selected machine is locked and cannot be deselected from below
                              return;
                            }
                            setSelectedMachines((prev) => {
                              const current = prev.includes(primaryMachine) ? prev : [primaryMachine, ...prev];
                              let updated;
                              if (current.includes(node.name)) {
                                updated = current.filter((p) => p !== node.name);
                              } else {
                                updated = [...current, node.name];
                              }
                              if (primaryMachine && !updated.includes(primaryMachine)) {
                                updated.unshift(primaryMachine);
                              }
                              // If a new machine was toggled on and its series isn't in memory yet, refetch it
                              if (!current.includes(node.name)) {
                                const hasData = metrics.some((m) => m.startsWith(`${node.name}_`) || m.startsWith(node.name));
                                if (!hasData) {
                                  refetch(updated.join(","), comparisonMetric, selectedBranch);
                                }
                              }
                              return updated;
                            });
                            setSelectAllActive(false);
                            setUserSelectionLocked(true);
                          }}
                          className={`metric-badge ${isActive ? "active" : ""}`}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: isActive ? `1px solid ${color}` : "1px solid #e6eef6",
                            background: isActive ? (color.startsWith("#") ? `${color}1A` : color) : "#fff",
                            color: isActive ? color : "#475569",
                            cursor: isPrimary ? "default" : "pointer",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontWeight: isPrimary ? 600 : undefined,
                          }}
                          title={isPrimary ? `${node.name} (Selected machine - locked)` : `Toggle ${node.name}`}
                        >
                          {isPrimary && (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ opacity: 0.8 }}
                            >
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                          )}
                          {node.name}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => scrollMachineList("right")}
                    className="scroll-arrow-button"
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      color: "#64748b",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#cbd5e1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                    type="button"
                    aria-label="Scroll right"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>


        <div style={{ width: "100%", display: "flex" }}>
          <section className="tables-wrapper">
            <OnOffChart
              username={localStorage.getItem("username")}
              dateRange={stableDateRange}
              branch={selectedBranch}
              machinesList={nodeOptions}
              scopeMachineNames={scopeMachineNames}
              scopeHierarchyPath={scopeHierarchyPath}
              currencyMode={currencyMode}
              tariffs={tariffs}
              hierarchyTree={fullHierarchyTree}
              onLoadingChange={(isLoading) =>
                updateChildGraphLoading("onOffChart", isLoading)
              }
            />
            <DrillDownPieChart
              username={localStorage.getItem("username")}
              dateRange={stableDateRange}
              scopeHierarchyPath={scopeHierarchyPath}
              machineColors={machineColors}
              currencyMode={currencyMode}
              tariffs={tariffs}
              onLoadingChange={(isLoading) =>
                updateChildGraphLoading("drillDownPieChart", isLoading)
              }
            />

            {/* Table 2: Metric Statistics
            <div className="analytics-table-card">
              <table className="dynamic-table">
                <thead>
                  <tr>
                    <th></th>
                    {columnVisibility.showGenerated && <th>Generated</th>}
                    {columnVisibility.showConsumed && <th>Consumed</th>}
                  </tr>
                </thead>
                <tbody>
                  {summaryData ? (
                    <>
                      <tr>
                        <td className="row-label">Average</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {(summaryData.Generated?.avg || 0).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>{(summaryData.Consumed?.avg || 0).toFixed(2)}</td>
                        )}
                      </tr>
                      <tr>
                        <td className="row-label">Minimum</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {Number(
                              typeof summaryData?.Generated?.min === "object"
                                ? summaryData?.Generated?.min?.value
                                : summaryData?.Generated?.min || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>
                            {Number(
                              typeof summaryData?.Consumed?.min === "object"
                                ? summaryData?.Consumed?.min?.value
                                : summaryData?.Consumed?.min || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                      </tr>
                      <tr>
                        <td className="row-label">Maximum</td>
                        {columnVisibility.showGenerated && (
                          <td>
                            {Number(
                              typeof summaryData?.Generated?.max === "object"
                                ? summaryData?.Generated?.max?.value
                                : summaryData?.Generated?.max || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                        {columnVisibility.showConsumed && (
                          <td>
                            {Number(
                              typeof summaryData?.Consumed?.max === "object"
                                ? summaryData?.Consumed?.max?.value
                                : summaryData?.Consumed?.max || 0,
                            ).toFixed(2)}
                          </td>
                        )}
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          Object.values(columnVisibility).filter(
                            (v) => v && v !== "showNet",
                          ).length + 1
                        }
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#94a3b8",
                        }}
                      >
                        {summaryLoading || loading
                          ? "Synchronizing system stats..."
                          : "No analytics available for this period"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div> */}
          </section>
        </div>
      </section>

      {(loading || error) && (
        <section className="selection-area" style={{ marginTop: 0 }}>
          <div style={{ display: "flex", gap: 8, marginTop: 0 }}>
            {loading && (
              <span
                className="metric-badge"
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#3b82f6",
                  borderColor: "#bfdbfe",
                }}
              >
                Processing...
              </span>
            )}
            {error && (
              <span
                className="metric-badge"
                style={{
                  backgroundColor: "#fff1f2",
                  color: "#f43f5e",
                  borderColor: "#fecdd3",
                }}
              >
                {error}
              </span>
            )}
          </div>
        </section>
      )}
      {/* Heatmap Section */}
      <section
        className="heatmap-section"
        style={{
          marginTop: 6,
          borderTop: "2px solid #f1f5f9",
        }}
      >
        {/* Sankey Diagram */}
        <SankeyDiagram
          username={localStorage.getItem("username")}
          dateRange={stableDateRange}
          scopeHierarchyPath={scopeHierarchyPath}
          machineColors={machineColors}
          currencyMode={currencyMode}
          tariffs={tariffs}
          onLoadingChange={(isLoading) =>
            updateChildGraphLoading("sankeyDiagram", isLoading)
          }
        />

        {/* Solar Plots Section */}
        {solarDetected && solarMachines.length > 0 && (
          <section
            className="chart-section"
            style={{ marginBottom: 20, position: "relative", minHeight: 320 }}
          >
            {isSolarLoading && (
              <BoltLoader
                progress={loadingProgress}
                label="Loading solar data…"
              />
            )}
            {solarMachines.length > 0 && (
              <div
                className="solar-machine-selector"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "16px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}>
                    <FieldHelp id="historical.solarMachine">Select Solar Machine:</FieldHelp>
                  </span>
                  {/* Was hand-rolled inline styles with symmetric 16px
                      padding, which left the native arrow sitting on top of the
                      option text and clipping it. `clean-select` is the page's
                      standard dropdown: it hides the native arrow, draws its own
                      chevron and reserves the room for it. */}
                  <select
                    className="clean-select"
                    value={selectedSolarMachine}
                    onChange={(e) => setSelectedSolarMachine(e.target.value)}
                    style={{
                      // Floor width so a short name like "Solar" still fills a
                      // sensible box; ceiling so a long one can't stretch the row.
                      minWidth: "170px",
                      maxWidth: "280px",
                    }}
                  >
                    {solarMachines.map((machine) => (
                      <option key={machine.name} value={machine.name}>
                        {machine.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="date-input-wrapper">
                  <input
                    type="datetime-local"
                    value={formatDateTimeLocal(solarDraft.start)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const next = new Date(e.target.value);
                      if (Number.isNaN(next.getTime())) return;
                      setSolarDraft((prev) => ({ ...prev, start: next }));
                    }}
                    onClick={(e) => e.target.showPicker?.()}
                    max={formatDateTimeLocal(new Date())}
                    step="1"
                    className="clean-date-input"
                    style={{ width: "240px" }}
                  />
                  <span style={{ color: "#cbd5e1", fontWeight: 700, margin: "0 4px" }}>
                    <FieldHelp id="historical.solarDateRange">TO</FieldHelp>
                  </span>
                  <input
                    type="datetime-local"
                    value={formatDateTimeLocal(solarDraft.end)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const next = new Date(e.target.value);
                      if (Number.isNaN(next.getTime())) return;
                      setSolarDraft((prev) => ({ ...prev, end: next }));
                    }}
                    onClick={(e) => e.target.showPicker?.()}
                    step="1"
                    className="clean-date-input"
                    style={{ width: "240px" }}
                  />
                  <div style={{ display: "flex", gap: 4, marginLeft: "4px" }}>
                    {quickRanges.map((range) => (
                      <button
                        key={range.value}
                        type="button"
                        onClick={() => {
                          const end = new Date();
                          if ([1, 3, 7, 30].includes(range.value)) {
                            end.setDate(end.getDate() - 1);
                          }
                          end.setHours(23, 59, 59, 999);
                          const start = new Date(end);
                          start.setDate(end.getDate() - (range.value - 1));
                          start.setHours(0, 0, 0, 0);
                          setSolarDraft({ start, end });
                          setSolarStartDate(start);
                          setSolarEndDate(end);
                        }}
                        className="mini-chip"
                        style={{ padding: "8px 14px" }}
                      >
                        <FieldHelp id="historical.solarDateRange">{range.label}</FieldHelp>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={applySolarRange}
                    disabled={!canApplySolarRange}
                    title={
                      isValidRange(solarDraft)
                        ? "Load solar data for this range"
                        : "Start date must be on or before the end date"
                    }
                    className="mini-chip"
                    style={{
                      padding: "8px 18px",
                      marginLeft: 8,
                      backgroundColor: canApplySolarRange ? "#009F8D" : "#e2e8f0",
                      color: canApplySolarRange ? "#fff" : "#94a3b8",
                      borderColor: canApplySolarRange ? "#009F8D" : "#e2e8f0",
                      fontWeight: 700,
                      cursor: canApplySolarRange ? "pointer" : "default",
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
            <div className="summary-cards-wrapper">
              {/* 1. Total Solar Export - Green */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#dcfce7", // Light green bg
                  border: "1px solid #bbf7d0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 21H21"
                        stroke="#15803d" // Dark green
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 21V7"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 21V3"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 21V11"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 21V5"
                        stroke="#15803d"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="15"
                        cy="16"
                        r="3"
                        fill="#15803d"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M15 14V18"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M13 16H17"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#15803d", marginTop: 12, fontSize: 13 }}
                >
                  <FieldHelp id="historical.solarGridExport">Total Grid Export</FieldHelp> ({unitLabel(isPkrMode, "Units")})
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#15803d", fontSize: 28 }}
                >
                  {/* Updated path from Grid_Export to Solar_Export */}
                  {formatAmount(
                    (solarData?.metrics?.Solar_Export || 0) *
                    (isPkrMode ? tariffs.exportCredit : 1),
                  )}{" "}
                  <span style={{ fontSize: 16 }}>{unitLabel(isPkrMode)}</span>
                </div>
              </div>

              {/* 2. Total Wapda Import - Red */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#fee2e2", // Light red bg
                  border: "1px solid #fecaca",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 21H21"
                        stroke="#b91c1c" // Dark red
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 21V7"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 21V3"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 21V11"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 21V5"
                        stroke="#b91c1c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="15"
                        cy="16"
                        r="3"
                        fill="#b91c1c"
                        stroke="#fff"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M13 16H17"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#b91c1c", marginTop: 12, fontSize: 13 }}
                >
                  <FieldHelp id="historical.solarGridImport">Total Grid Import</FieldHelp> ({unitLabel(isPkrMode, "Units")})
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#b91c1c", fontSize: 28 }}
                >
                  {/* Updated path */}
                  {formatAmount(
                    (solarData?.metrics?.Grid_Import || 0) *
                    (isPkrMode ? tariffs.grid : 1),
                  )}{" "}
                  <span style={{ fontSize: 16 }}>{unitLabel(isPkrMode)}</span>
                </div>
              </div>

              {/* 3. Total Solar Generation - Yellow */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#fef9c3", // Light yellow bg
                  border: "1px solid #fde047",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4V2"
                        stroke="#ca8a04" // Dark yellow/gold
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.636 5.636L4.222 4.222"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.364 5.636L19.778 4.222"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 12H22"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12H4"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 18H18"
                        stroke="#ca8a04"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#ca8a04", marginTop: 12, fontSize: 13 }}
                >
                  <FieldHelp id="historical.solarProduction">Total Solar Production</FieldHelp> ({unitLabel(isPkrMode, "Units")})
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#ca8a04", fontSize: 28 }}
                >
                  {/* Show actual solar production from comparison data */}
                  {formatAmount(
                    (solarProductionData?.data?.reduce((sum, item) => {
                      const val = Number(item.actual_generation_kwh);
                      return Number.isFinite(val) ? sum + val : sum;
                    }, 0) || 0) * (isPkrMode ? tariffs.solar : 1),
                  )}{" "}
                  <span style={{ fontSize: 16 }}>{unitLabel(isPkrMode)}</span>
                </div>
              </div>

              {/* 4. Total Load Consumption - Purple */}
              <div
                className="summary-card"
                style={{
                  backgroundColor: "#f3e8ff", // Light purple bg
                  border: "1px solid #d8b4fe",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ opacity: 1 }}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="4"
                        y="12"
                        width="4"
                        height="8"
                        rx="1"
                        stroke="#581c87" // Dark purple
                        strokeWidth="2"
                      />
                      <rect
                        x="10"
                        y="8"
                        width="4"
                        height="12"
                        rx="1"
                        stroke="#581c87"
                        strokeWidth="2"
                      />
                      <rect
                        x="16"
                        y="4"
                        width="4"
                        height="16"
                        rx="1"
                        stroke="#581c87"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  className="summary-card-title"
                  style={{ color: "#581c87", marginTop: 12, fontSize: 13 }}
                >
                  <FieldHelp id="historical.loadConsumption">Total Load Consumption</FieldHelp> ({unitLabel(isPkrMode, "Units")})
                </div>
                <div
                  className="summary-card-value"
                  style={{ color: "#00008b", fontSize: 28 }}
                >
                  {/* Updated path */}
                  {formatAmount(
                    (solarData?.metrics?.Load_Consumption || 0) *
                    (isPkrMode ? tariffs.grid : 1),
                  )}{" "}
                  <span style={{ fontSize: 16 }}>{unitLabel(isPkrMode)}</span>
                </div>
              </div>
            </div>

            {/* Solar Graph */}
            {(() => {
              // Transform the new API structure for the SolarGraph component
              const formattedSolarData =
                solarData?.data?.["Date & Time"]?.map((time, index) => ({
                  timestamp: time,
                  Grid_Import_kWh:
                    solarData.data["GRID"]?.[index] > 0
                      ? solarData.data["GRID"][index]
                      : 0,
                  Grid_Export_kWh:
                    solarData.data["GRID"]?.[index] < 0
                      ? Math.abs(solarData.data["GRID"][index])
                      : 0,
                  Solar_Generated_kWh: Math.abs(
                    solarData.data["SOLAR"]?.[index] || 0,
                  ),
                })) || [];

              return (
                <SolarGraph
                  data={formattedSolarData}
                  dateRange={{ start: solarStartDate, end: solarEndDate }}
                  granularity={solarGranularity}
                  onGranularityChange={setSolarGranularity}
                />
              );
            })()}

            {/* Solar Production Comparison */}
            {solarDetected && (
              <SolarProductionComparison
                data={solarProductionData?.data || []}
                dateRange={{ start: solarStartDate, end: solarEndDate }}
                currencyMode={currencyMode}
                tariffs={tariffs}
              />
            )}
          </section>
        )}

        <div className="heatmap-wrapper" style={{ display: 'none' }}>
          {/* This section is moved into the main analytics card but kept here (hidden) for now or removed if safe */}
        </div>

        {/* Comparison Graphs Section */}
        <div className="comparison-graphs-section">
          <ComparisonGraph
            machinesList={nodeOptions}
            title="Machine 1"
            loaderKey="comparisonGraphA"
            onLoadingChange={updateChildGraphLoading}
            currencyMode={currencyMode}
            tariffs={tariffs}
            hierarchyTree={fullHierarchyTree}
          />
          <ComparisonGraph
            machinesList={nodeOptions}
            title="Machine 2"
            loaderKey="comparisonGraphB"
            onLoadingChange={updateChildGraphLoading}
            currencyMode={currencyMode}
            tariffs={tariffs}
            hierarchyTree={fullHierarchyTree}
          />
        </div>
      </section>
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

const isValidRange = (range) =>
  range?.start instanceof Date &&
  range?.end instanceof Date &&
  !Number.isNaN(range.start.getTime()) &&
  !Number.isNaN(range.end.getTime()) &&
  range.start <= range.end;

const formatDateTimeLocal = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const getUnitForMetric = (metricName) => {
  if (!metricName) return "kW";
  const lower = (metricName || "").toLowerCase();
  const option = comparisonMetricOptions.find((opt) =>
    opt.matchers.some((matcher) => lower.includes(matcher.toLowerCase().trim())),
  );
  return option ? option.unit : "kW";
};

export default HistoricalTab;
